/**
 * ===========================================================================
 *  NUSANTARA WARRIOR — MOVESET TABLE
 * ===========================================================================
 *
 * Every attack in the game is DATA. `src/combat/combat.js` resolves it and
 * `src/game/player.js` sequences it; neither of them knows what a C4 is.
 * Adding a weapon is adding an entry here.
 *
 * ---------------------------------------------------------------------------
 * TIMING
 * ---------------------------------------------------------------------------
 * Frame counts are 60ths of a second and follow the DW reference plate
 * (section C): windup 6-14, active 3-6, recovery 8-20 for the light chain.
 * Big finishers, charge attacks and the musou deliberately run past those
 * bands the way the real games do — a C6 or a ground slam telegraphs for
 * 25-40 frames and that anticipation is what sells the weight.
 *
 *   windup    frames from the input to the first ACTIVE frame
 *   active    frames the blade is live (may span two windows, see C4)
 *   recovery  frames after the blade closes; cancellable from `cancel.at`
 *   rate      clip playback multiplier. The clips in src/anim/clips.js are
 *             authored at a readable speed; `rate` time-warps each one so its
 *             own hitboxOn/hitboxOff markers land on the frames declared here.
 *
 * The hitbox is switched on and off by the clip's own EVENT MARKERS
 * (`hitboxOn` / `hitboxOff`, schema at the top of src/anim/clips.js). The
 * numbers below are the design contract and the state-machine phases; the
 * markers are the wire. `combat.js` merges them: the marker says WHEN, this
 * table says WHAT.
 *
 * ---------------------------------------------------------------------------
 * THE HITBOX IS A SWEPT ARC
 * ---------------------------------------------------------------------------
 *   arc.half   half-angle in degrees either side of `arc.bias`. 180 = a full
 *              360 sweep. This is the WHOLE angular span the blade travels
 *              during the active window, not an instantaneous wedge — the
 *              resolver tests the entire span on the frame the window opens so
 *              that one sweep hits every body inside it in the SAME frame.
 *              6-15 bodies flying at once is the point of the genre.
 *   arc.bias   centre of the span, degrees off the attacker's facing
 *              (+ = the attacker's right). A right-to-left sweep biases left.
 *   arc.r0/r1  inner / outer radius in metres from the pelvis. r0 > 0 gives
 *              polearms their dead zone at the haft.
 *              r1 IS NOT THE BLADE LENGTH. It is the radius of the SWEPT arc:
 *              the resolver tests the whole angular span on the frame the
 *              window opens, and a body counts as hit when its CENTRE is
 *              within r1 + its own radius (~0.42 m). Measured blade-tip reach
 *              from the pelvis, through the shipped clips and the shipped
 *              weapon sockets, runs 0.9-2.2 m depending on weapon and move,
 *              so r1 sits about 1.5x the instantaneous tip reach across the
 *              whole table — deliberately, because the AI rings its peons at
 *              2.2-3.5 m (src/ai/grunt.js R_RING) and a sweep that only
 *              reached the visible steel would clear nothing. Keep that ratio
 *              roughly CONSTANT between weapons; a move whose r1 is twice its
 *              neighbours' ratio is a lie, not a long weapon.
 *   arc.y0/y1  height band in metres RELATIVE TO THE ATTACKER'S FEET. An air
 *              slam has a negative y0; a rising launcher has a tall y1.
 *   arc.sweep  [fromDeg, toDeg] — the blade's visual travel, handed to VFX so
 *              the slash ribbon points the right way. Not used by the test.
 *
 * ---------------------------------------------------------------------------
 * IMPACT
 * ---------------------------------------------------------------------------
 *   damage   hit points. A grunt has 40.
 *   poise    poise damage. Breaking poise upgrades a flinch into a stagger.
 *   knock    knockback in METRES TRAVELLED (the resolver converts to an
 *            impulse through `drag`). Plate D: 1.5-4 m normal, 6-12 m launcher.
 *   lift     launch height in METRES. The resolver solves the ballistic
 *            velocity from it, so a 2.2 m lift is ~0.9 s of hang time — long
 *            enough for the 2-4 follow-up hits the plate asks for.
 *   drag     knockback decay constant. Lower = further, faster, flatter.
 *   radial   0 = everything flies along the attacker's facing, 1 = everything
 *            flies straight away from him. 360 sweeps want 1.
 *   hitstop  class name, resolved through HITSTOP below (plate D: 2-4 frames
 *            light, 5-8 heavy, 8-12 + slowmo on a musou finisher).
 *
 * ---------------------------------------------------------------------------
 * ROOT MOTION
 * ---------------------------------------------------------------------------
 *   root.d          metres travelled forward over the move (plate C: 0.5-2.0
 *                   normal, further on charges).
 *   root.a/root.b   the frame window the displacement is spread over.
 *   root.ease       easing curve name from core/mathx.js.
 * The hero's visual root motion is switched OFF (`anim.applyRootMotion=false`)
 * so the mesh never double-counts this.
 *
 * ---------------------------------------------------------------------------
 * BRANCHING — the genre's signature
 * ---------------------------------------------------------------------------
 * `chain` is C1..C6. From any chain position the CHARGE button branches into a
 * different move with a different PROPERTY:
 *   ch1 launcher   ch2 stun   ch3 ground-slam AoE   ch4 wide 360   ch5 wall-slam
 * `moves[x].next` is the light follow-up, `moves[x].charge` the branch.
 *
 * Each of those five has its OWN CLIP PER WEAPON — the glaive keeps
 * `charge_launcher` / `charge_stun` / `charge_slam` / `charge_spin360` /
 * `charge_lance`, the spear runs `spear_ch1_rise` .. `spear_ch5_couch` and the
 * sabres `sabre_ch1_uppair` .. `sabre_ch5_barge`, all in src/anim/clips.js.
 * Until those ten existed all three sets played the SAME five separated only
 * by `rate`: measured with tools/clip-shape.mjs the hips turned an identical
 * 184 / 126 / 37 / 804 / 32 degrees whichever weapon was held. The rates below
 * stay, but they are now a speed difference on top of a real shape difference
 * rather than the only difference. They
 * used to replay light-chain clips at a slower `rate`, which made all five
 * swept boxes match a light-chain hit to within 0.02 m; measured now, on the
 * glaive, against the clip each one replaced:
 *   ch1 launcher   1.71 x 3.18 x 3.05 m, tip 0.02 -> 3.20 m   (was = c3)
 *   ch2 stun       3.33 x 1.47 x 3.17 m, tip stays 0.85-2.32  (was = c2)
 *   ch3 slam       1.26 x 2.78 x 3.30 m, tip ends at -0.06    (was = c5)
 *   ch4 360        4.17 x 1.24 x 4.20 m, hips turn 759 deg    (was = c4, 410)
 *   ch5 wall-slam  1.25 x 1.92 x 2.01 m, hips turn 10 deg     (was = c6)
 * ===========================================================================
 */

/** One frame, in seconds. */
export const F = 1 / 60;

/** Hitstop classes, in seconds of frozen simulation. Plate D. */
export const HITSTOP = {
  none: 0,
  light: 3 * F,      // 3 frames
  med: 5 * F,        // 5 frames
  heavy: 7 * F,      // 7 frames
  huge: 10 * F,      // 10 frames
  musou: 12 * F,     // 12 frames + a slowmo tail, applied by the player
};

/** Explicit ownership for presentation feedback; gameplay never infers this
 * from a semantic name or a free-form tag string. */
export const PRESENTATION = {
  COMBAT_VFX: 1 << 0,       // combat emits move.vfx once for a connected sweep
  MUSOU_FINISHER: 1 << 1,   // combat solely owns impact stop/shake/image/flash
  QUIET_IMPACT: 1 << 2,     // repeated pulses may spark but never freeze the sim
};

/** Poise pools by entity KIND index. Index with `entities.kind[id]`. */
export const POISE_BY_KIND = [220, 20, 46, 110, 190, 20, 110, 16, 60, 400];

/** Reaction timings, seconds. Shared by every weapon. */
export const REACT = {
  flinch: 0.30,      // hit_light
  stagger: 0.58,     // hit_heavy, poise broken
  launchHold: 0.24,  // launch clip before the airborne loop takes over
  down: 0.80,        // face down on the floor
  getup: 0.95,
  guardBreak: 0.85,
};

/* ------------------------------------------------------------------ factory */

const A = (o) => Object.assign({ half: 90, bias: 0, r0: 0.35, r1: 2.8, y0: -0.05, y1: 2.15, sweep: null }, o);

let _seq = 0;
/** Build one move, filling in every default so the resolver never branches. */
function mv(o) {
  const m = {
    seq: _seq++,
    id: o.id,
    label: o.label || o.id,
    clip: o.clip,
    rate: o.rate ?? 1,
    windup: o.windup ?? 8,
    active: o.active ?? 5,
    recovery: o.recovery ?? 14,
    arc: A(o.arc),
    damage: o.damage ?? 30,
    poise: o.poise ?? 12,
    knock: o.knock ?? 1.8,
    drag: o.drag ?? 4.2,
    lift: o.lift ?? 0,
    launch: !!o.launch,
    radial: o.radial ?? 0.55,
    stun: o.stun ?? 0,
    gb: !!o.gb,
    unblockable: !!o.unblockable,
    hitstop: o.hitstop || 'light',
    shake: o.shake ?? 0.34,
    kick: o.kick ?? 0.25,
    hitCap: o.hitCap ?? 28,
    root: Object.assign({ d: 0.8, a: 4, b: 18, ease: 'outQuint' }, o.root),
    cancel: Object.assign({ at: 0, into: null }, o.cancel),
    next: o.next || null,
    charge: o.charge || null,
    vfx: o.vfx || 'slash',
    presentation: o.presentation ?? PRESENTATION.COMBAT_VFX,
    sfx: o.sfx || 'swing',
    voice: o.voice || 'effort',
    /** Extra hitbox windows this move opens on top of the clip's markers.
     *  `{ at, dur }` in move-frames; `arc`/`damage`... override per window. */
    extra: o.extra || null,
    /** Ignore the clip's own hitboxOn/Off markers and use `extra` only. */
    ownWindows: !!o.ownWindows,
    airborne: !!o.airborne,     // may only be started in the air
    dive: !!o.dive,             // slams the attacker down to the ground
    invuln: o.invuln ?? 0,      // seconds of i-frames from move start
    tags: o.tags || '',
  };
  m.total = m.windup + m.active + m.recovery;
  // Default cancel point: a third of the way into recovery, DW-style — early
  // enough that the chain feels responsive, late enough to read the follow-through.
  if (!m.cancel.at) m.cancel.at = m.windup + m.active + Math.round(m.recovery * 0.34);
  return m;
}

const table = (arr) => { const o = {}; for (const m of arr) o[m.id] = m; return o; };

/* =========================================================================
 *  GREAT GLAIVE — the hero's weapon (guan dao).
 *  Long reach, heavy body rotation, the widest sweeps in the game.
 * ========================================================================= */

const GLAIVE = table([
  /* --- light chain C1..C6 : escalating reach and body rotation ---------- */
  mv({
    id: 'c1', label: 'Rising Cut', clip: 'attack_light_1', rate: 1.20,
    windup: 9, active: 6, recovery: 12,
    arc: A({ half: 78, bias: -6, r0: 0.45, r1: 3.00, y0: 0.05, y1: 2.05, sweep: [82, -74] }),
    damage: 34, poise: 14, knock: 1.6, hitstop: 'light', shake: 0.26,
    root: { d: 0.85, a: 5, b: 17, ease: 'outQuint' },
    next: 'c2', charge: 'ch2', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'c2', label: 'Reverse Sweep', clip: 'attack_light_2', rate: 1.15,
    windup: 8, active: 6, recovery: 12,
    arc: A({ half: 84, bias: 8, r0: 0.45, r1: 3.05, y0: 0.00, y1: 1.90, sweep: [-88, 80] }),
    damage: 36, poise: 15, knock: 1.7, hitstop: 'light', shake: 0.28,
    root: { d: 0.80, a: 4, b: 16, ease: 'outQuint' },
    next: 'c3', charge: 'ch3', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'c3', label: 'Rising Diagonal', clip: 'attack_light_3', rate: 1.30,
    windup: 10, active: 7, recovery: 12,
    arc: A({ half: 92, bias: -10, r0: 0.45, r1: 3.25, y0: 0.15, y1: 2.45, sweep: [96, -84] }),
    damage: 42, poise: 20, knock: 2.1, lift: 0.35, hitstop: 'med', shake: 0.36,
    root: { d: 1.15, a: 5, b: 18, ease: 'outQuint' },
    next: 'c4', charge: 'ch4', vfx: 'slashBig', sfx: 'swing_heavy',
  }),
  /* C4 — the full spin. The clip carries TWO hitbox windows; the second one
   * launches. This is the crowd-clearer: a 360 sweep at 3.4 m must put six or
   * more bodies in the air on the same frame. */
  mv({
    id: 'c4', label: 'Whirling Moon', clip: 'attack_light_4', rate: 1.10,
    windup: 13, active: 23, recovery: 14,
    // r1 3.75: the AI rings its peons at 2.2-3.5 m (and pushes to ~4.9 when
    // crowded), so a glaive spin that is meant to CLEAR the ring has to reach
    // past the far side of it. See src/ai/grunt.js R_RING / store.ringR.
    arc: A({ half: 180, r0: 0.35, r1: 3.75, y0: -0.10, y1: 2.30, sweep: [0, 360] }),
    damage: 54, poise: 34, knock: 3.4, lift: 1.15, launch: true, radial: 1,
    drag: 3.8, hitstop: 'heavy', shake: 0.55, kick: 0.5, hitCap: 32,
    root: { d: 1.45, a: 8, b: 30, ease: 'outCubic' },
    next: 'c5', charge: 'ch5', vfx: 'slashSpin', sfx: 'swing_heavy', voice: 'shout',
  }),
  mv({
    id: 'c5', label: 'Mountain Splitter', clip: 'attack_heavy', rate: 1.25,
    windup: 23, active: 8, recovery: 18,
    arc: A({ half: 108, r0: 0.30, r1: 3.50, y0: -0.15, y1: 2.20, sweep: [0, 0] }),
    damage: 78, poise: 55, knock: 3.2, lift: 1.55, launch: true, gb: true,
    drag: 3.6, hitstop: 'heavy', shake: 0.62, kick: 0.6,
    root: { d: 1.30, a: 12, b: 30, ease: 'outQuart' },
    next: 'c6', charge: 'ch5', vfx: 'slam', sfx: 'swing_heavy', voice: 'shout',
  }),
  /* C6 — the finisher. Longest wind in the set, widest arc, everything goes. */
  mv({
    id: 'c6', label: 'Sundering Banner', clip: 'attack_charge', rate: 1.35,
    windup: 30, active: 14, recovery: 11,
    arc: A({ half: 165, r0: 0.35, r1: 3.95, y0: -0.10, y1: 2.45, sweep: [-170, 170] }),
    damage: 96, poise: 80, knock: 5.5, lift: 2.00, launch: true, gb: true, radial: 0.9,
    drag: 3.2, hitstop: 'huge', shake: 0.85, kick: 0.85, hitCap: 32,
    root: { d: 2.20, a: 18, b: 44, ease: 'outQuint' },
    next: null, charge: 'ch5', vfx: 'slashHuge', sfx: 'swing_huge', voice: 'shout',
  }),

  /* --- charge branch: a different PROPERTY at every chain position ------ */
  /* ch1 — LAUNCHER. Scoops the rank off its feet for a juggle. */
  mv({
    id: 'ch1', label: 'Crescent Lift', clip: 'charge_launcher', rate: 1.00,
    windup: 13, active: 9, recovery: 15,
    arc: A({ half: 96, bias: -8, r0: 0.30, r1: 3.15, y0: -0.20, y1: 2.90, sweep: [100, -90] }),
    damage: 48, poise: 46, knock: 1.0, lift: 3.00, launch: true, radial: 0.35,
    drag: 4.6, hitstop: 'heavy', shake: 0.42, kick: 0.35,
    root: { d: 1.00, a: 6, b: 21, ease: 'outQuint' },
    charge: 'ch2', vfx: 'launchArc', sfx: 'swing_heavy', voice: 'shout', tags: 'launcher',
  }),
  /* ch2 — STUN. Barely moves them; leaves them wide open. */
  mv({
    id: 'ch2', label: 'Haft Crack', clip: 'charge_stun', rate: 1.00,
    windup: 11, active: 8, recovery: 17,
    arc: A({ half: 72, bias: 8, r0: 0.35, r1: 3.00, y0: 0.10, y1: 2.10, sweep: [-66, 74] }),
    damage: 40, poise: 62, knock: 0.30, stun: 1.55, radial: 0.7,
    hitstop: 'med', shake: 0.38, kick: 0.3,
    root: { d: 0.70, a: 5, b: 19, ease: 'outQuint' },
    charge: 'ch3', vfx: 'stunBurst', sfx: 'impact_blunt', voice: 'shout', tags: 'stun',
  }),
  /* ch3 — GROUND SLAM. A 5 m radial shockwave with a delayed second ring. */
  mv({
    id: 'ch3', label: 'Earthbreaker', clip: 'charge_slam', rate: 1.00,
    windup: 29, active: 10, recovery: 21,
    arc: A({ half: 180, r0: 0.00, r1: 5.00, y0: -0.30, y1: 1.70, sweep: [0, 360] }),
    damage: 70, poise: 74, knock: 3.6, lift: 1.40, launch: true, gb: true, radial: 1,
    drag: 3.4, hitstop: 'huge', shake: 0.95, kick: 0.75, hitCap: 36,
    root: { d: 1.10, a: 14, b: 30, ease: 'outQuart' },
    // the shockwave that runs out from the impact, 8 frames behind the blade
    extra: [{ at: 47, dur: 5, arc: A({ half: 180, r0: 3.4, r1: 7.6, y0: -0.30, y1: 1.40 }),
      damage: 34, poise: 30, knock: 2.6, lift: 0.75, radial: 1, hitstop: 'none', vfx: 'shock' }],
    charge: 'ch4', vfx: 'groundSlam', sfx: 'impact_slam', voice: 'shout', tags: 'aoe',
  }),
  /* ch4 — WIDE 360. Two full revolutions, the biggest crowd-clear that is not
   * a musou. Deliberately slow to recover. */
  mv({
    id: 'ch4', label: 'Storm of Blades', clip: 'charge_spin360', rate: 1.00,
    windup: 15, active: 27, recovery: 16,
    arc: A({ half: 180, r0: 0.30, r1: 4.60, y0: 0.00, y1: 2.40, sweep: [0, 720] }),
    damage: 58, poise: 56, knock: 4.6, lift: 0.85, launch: true, radial: 1,
    drag: 3.4, hitstop: 'heavy', shake: 0.7, kick: 0.55, hitCap: 40,
    root: { d: 1.05, a: 10, b: 40, ease: 'outCubic' },
    charge: 'ch5', vfx: 'slashSpin', sfx: 'swing_huge', voice: 'shout', tags: 'sweep360',
  }),
  /* ch5 — WALL SLAM. One target line, 11 m of travel, bounces off the ground. */
  mv({
    id: 'ch5', label: 'Banner Charge', clip: 'charge_lance', rate: 1.00,
    windup: 41, active: 15, recovery: 19,
    arc: A({ half: 58, r0: 0.30, r1: 4.80, y0: -0.05, y1: 2.40, sweep: [0, 0] }),
    damage: 110, poise: 95, knock: 11.0, lift: 1.80, launch: true, gb: true, radial: 0.15,
    drag: 2.6, hitstop: 'huge', shake: 1.0, kick: 1.0,
    root: { d: 3.00, a: 22, b: 56, ease: 'outQuint' },
    charge: null, vfx: 'pierce', sfx: 'swing_huge', voice: 'shout', tags: 'wallSlam',
  }),

  /* --- situational ------------------------------------------------------ */
  mv({
    id: 'dash', label: 'Running Thrust', clip: 'charge_lance', rate: 2.06,
    windup: 20, active: 9, recovery: 8,
    arc: A({ half: 62, r0: 0.30, r1: 3.50, y0: 0.00, y1: 2.10, sweep: [0, 0] }),
    damage: 46, poise: 30, knock: 2.8, lift: 0.55, radial: 0.2,
    hitstop: 'med', shake: 0.4, kick: 0.45,
    root: { d: 3.40, a: 6, b: 28, ease: 'outQuart' },
    next: 'c2', charge: 'ch3', vfx: 'pierce', sfx: 'swing_heavy',
  }),
  mv({
    id: 'air1', label: 'Falling Cut', clip: 'attack_light_1', rate: 1.30, airborne: true,
    windup: 7, active: 6, recovery: 14,
    arc: A({ half: 84, bias: -6, r0: 0.35, r1: 2.95, y0: -1.60, y1: 1.60, sweep: [80, -78] }),
    damage: 38, poise: 16, knock: 1.2, lift: 0.55, radial: 0.5,
    hitstop: 'light', shake: 0.26,
    root: { d: 0.55, a: 4, b: 14, ease: 'outQuint' },
    next: 'air2', charge: 'airSlam', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'air2', label: 'Falling Sweep', clip: 'attack_light_2', rate: 1.25, airborne: true,
    windup: 7, active: 6, recovery: 11,
    arc: A({ half: 92, bias: 8, r0: 0.35, r1: 3.05, y0: -1.80, y1: 1.50, sweep: [-90, 86] }),
    damage: 44, poise: 22, knock: 1.4, lift: 0.65, radial: 0.6,
    hitstop: 'light', shake: 0.3,
    root: { d: 0.55, a: 4, b: 15, ease: 'outQuint' },
    next: null, charge: 'airSlam', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'airSlam', label: 'Meteor Drop', clip: 'charge_slam', rate: 1.42, airborne: true, dive: true,
    windup: 21, active: 7, recovery: 15,
    arc: A({ half: 180, r0: 0.00, r1: 3.80, y0: -2.60, y1: 1.20, sweep: [0, 360] }),
    damage: 82, poise: 70, knock: 3.0, lift: 1.10, launch: true, gb: true, radial: 1,
    drag: 3.4, hitstop: 'huge', shake: 0.9, kick: 0.7, hitCap: 32,
    root: { d: 0.40, a: 10, b: 34, ease: 'outQuad' },
    vfx: 'groundSlam', sfx: 'impact_slam', voice: 'shout', tags: 'aoe',
  }),

  /* --- musou finisher (the flurry itself is pulsed, see MUSOU below) ----- */
  mv({
    id: 'musouFinish', label: 'Banner of the Fallen', clip: 'musou_finish', rate: 0.90,
    // 37 = the frame `musou_finish` plants the blade in the ground at rate 0.90
    // (clip t 0.48). The windows are `ownWindows`, so they have to be pinned to
    // the animation by hand: if the clip is retimed, retime these with it.
    windup: 37, active: 16, recovery: 21, ownWindows: true,
    arc: A({ half: 180, r0: 0.00, r1: 7.50, y0: -0.40, y1: 3.20, sweep: [0, 360] }),
    damage: 240, poise: 300, knock: 9.0, lift: 3.20, launch: true, gb: true, radial: 1,
    drag: 2.8, hitstop: 'musou', shake: 1.2, kick: 1.2, hitCap: 48,
    root: { d: 1.60, a: 20, b: 48, ease: 'outQuint' },
    extra: [
      { at: 37, dur: 4 },
      { at: 44, dur: 4, arc: A({ half: 180, r0: 3.0, r1: 11.0, y0: -0.40, y1: 3.20 }), damage: 140, knock: 7.0, lift: 2.2 },
    ],
    vfx: 'musouFinish', presentation: PRESENTATION.COMBAT_VFX | PRESENTATION.MUSOU_FINISHER,
    sfx: 'impact_musou', voice: 'shout', tags: 'musou',
  }),
]);

/* =========================================================================
 *  SPEAR — long, thin, fast. Thrusts instead of sweeps: narrow arcs, big
 *  inner dead zone, and the longest THREATENED LINE in the game.
 *
 *  Read that carefully, because the old table claimed something it could not
 *  show. The spear MESH is the shortest of the three (tip 1.37 m from the grip
 *  against the glaive's 1.79 and the sabre's 1.02 — src/chars/weapons.js), so
 *  no spear pose can put more steel in the air than the glaive at one instant.
 *  What the spear does instead is TRAVEL: `spear_skewer` extends the point to
 *  2.19 m from the pelvis — measured, and the furthest of any single strike in
 *  the table — and the root carries the hero another 2.6 m forward while the
 *  window is open, so the line it threatens is far longer than anything the
 *  glaive reaches. The declared radii follow that: c5 4.20 (was 5.20) and
 *  ch5 5.20 (was 6.20), which puts the spear's r1-to-tip ratio back in line
 *  with the rest of the table instead of 2.4x it.
 *  A longer spear mesh is requested in docs/REQUESTS.md; if it lands, these
 *  radii can go back up.
 * ========================================================================= */

const SPEAR = table([
  mv({
    id: 'c1', label: 'Jab', clip: 'spear_thrust_1', rate: 1.45,
    windup: 6, active: 4, recovery: 10,
    arc: A({ half: 30, r0: 0.80, r1: 3.30, y0: 0.40, y1: 1.90, sweep: [10, -10] }),
    damage: 26, poise: 9, knock: 1.2, radial: 0.2, hitstop: 'light', shake: 0.2,
    root: { d: 0.75, a: 3, b: 12, ease: 'outQuint' }, hitCap: 6,
    next: 'c2', charge: 'ch2', vfx: 'pierce', sfx: 'swing_light',
  }),
  mv({
    id: 'c2', label: 'Second Jab', clip: 'spear_thrust_2', rate: 1.45,
    windup: 6, active: 4, recovery: 10,
    arc: A({ half: 32, r0: 0.80, r1: 3.45, y0: 0.35, y1: 1.85, sweep: [-12, 12] }),
    damage: 28, poise: 10, knock: 1.3, radial: 0.2, hitstop: 'light', shake: 0.22,
    root: { d: 0.75, a: 3, b: 12, ease: 'outQuint' }, hitCap: 6,
    next: 'c3', charge: 'ch3', vfx: 'pierce', sfx: 'swing_light',
  }),
  mv({
    id: 'c3', label: 'Haft Sweep', clip: 'spear_haft_sweep', rate: 1.30,
    windup: 9, active: 6, recovery: 13,
    arc: A({ half: 100, bias: -8, r0: 0.50, r1: 3.20, y0: 0.10, y1: 2.10, sweep: [104, -96] }),
    damage: 34, poise: 22, knock: 2.2, hitstop: 'med', shake: 0.34,
    root: { d: 0.95, a: 5, b: 18, ease: 'outQuint' },
    next: 'c4', charge: 'ch4', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'c4', label: 'Wheeling Haft', clip: 'spear_haft_spin', rate: 1.20,
    windup: 12, active: 21, recovery: 13,
    arc: A({ half: 180, r0: 0.40, r1: 3.60, y0: -0.05, y1: 2.20, sweep: [0, 360] }),
    damage: 44, poise: 30, knock: 3.0, lift: 0.95, launch: true, radial: 1,
    drag: 3.9, hitstop: 'heavy', shake: 0.5, kick: 0.45, hitCap: 32,
    root: { d: 1.20, a: 8, b: 28, ease: 'outCubic' },
    next: 'c5', charge: 'ch5', vfx: 'slashSpin', sfx: 'swing_heavy', voice: 'shout',
  }),
  mv({
    id: 'c5', label: 'Skewer', clip: 'spear_skewer', rate: 1.15,
    windup: 22, active: 10, recovery: 8,
    arc: A({ half: 34, r0: 0.60, r1: 4.20, y0: 0.20, y1: 2.10, sweep: [0, 0] }),
    damage: 82, poise: 60, knock: 4.2, lift: 0.9, launch: true, gb: true, radial: 0.1,
    drag: 3.2, hitstop: 'heavy', shake: 0.6, kick: 0.7,
    root: { d: 2.60, a: 12, b: 34, ease: 'outQuint' }, hitCap: 10,
    next: 'c6', charge: 'ch5', vfx: 'pierce', sfx: 'swing_huge', voice: 'shout',
  }),
  mv({
    id: 'c6', label: 'Dragon Tail', clip: 'spear_dragon_tail', rate: 1.00,
    windup: 16, active: 30, recovery: 16,
    arc: A({ half: 180, r0: 0.40, r1: 4.40, y0: -0.10, y1: 2.40, sweep: [0, 720] }),
    damage: 90, poise: 90, knock: 5.0, lift: 1.9, launch: true, gb: true, radial: 1,
    drag: 3.1, hitstop: 'huge', shake: 0.85, kick: 0.8, hitCap: 36,
    root: { d: 1.60, a: 14, b: 46, ease: 'outCubic' },
    charge: 'ch5', vfx: 'slashHuge', sfx: 'swing_huge', voice: 'shout',
  }),

  mv({
    id: 'ch1', label: 'Rising Point', clip: 'spear_ch1_rise', rate: 1.10,
    windup: 12, active: 8, recovery: 15,
    arc: A({ half: 48, r0: 0.50, r1: 3.40, y0: -0.20, y1: 3.10, sweep: [30, -30] }),
    damage: 40, poise: 44, knock: 0.9, lift: 3.20, launch: true, radial: 0.3,
    drag: 4.6, hitstop: 'heavy', shake: 0.4,
    root: { d: 0.95, a: 6, b: 20, ease: 'outQuint' }, hitCap: 10,
    charge: 'ch2', vfx: 'launchArc', sfx: 'swing_heavy', voice: 'shout', tags: 'launcher',
  }),
  mv({
    id: 'ch2', label: 'Butt Strike', clip: 'spear_ch2_butt', rate: 1.15,
    windup: 10, active: 7, recovery: 16,
    arc: A({ half: 70, r0: 0.30, r1: 2.40, y0: 0.20, y1: 2.00, sweep: [-60, 60] }),
    damage: 34, poise: 66, knock: 0.25, stun: 1.7, radial: 0.7,
    hitstop: 'med', shake: 0.36,
    root: { d: 0.60, a: 4, b: 18, ease: 'outQuint' },
    charge: 'ch3', vfx: 'stunBurst', sfx: 'impact_blunt', tags: 'stun',
  }),
  mv({
    id: 'ch3', label: 'Quake Thrust', clip: 'spear_ch3_quake', rate: 1.10,
    windup: 27, active: 9, recovery: 20,
    arc: A({ half: 180, r0: 0.00, r1: 4.40, y0: -0.30, y1: 1.60, sweep: [0, 360] }),
    damage: 60, poise: 70, knock: 3.2, lift: 1.25, launch: true, gb: true, radial: 1,
    drag: 3.5, hitstop: 'huge', shake: 0.9, kick: 0.7, hitCap: 32,
    root: { d: 1.00, a: 12, b: 28, ease: 'outQuart' },
    extra: [{ at: 44, dur: 5, arc: A({ half: 180, r0: 3.0, r1: 6.8, y0: -0.30, y1: 1.40 }),
      damage: 30, poise: 26, knock: 2.4, lift: 0.7, radial: 1, hitstop: 'none', vfx: 'shock' }],
    charge: 'ch4', vfx: 'groundSlam', sfx: 'impact_slam', voice: 'shout', tags: 'aoe',
  }),
  mv({
    id: 'ch4', label: 'Circle of Spears', clip: 'spear_ch4_circle', rate: 1.00,
    windup: 15, active: 28, recovery: 16,
    arc: A({ half: 180, r0: 0.30, r1: 5.10, y0: 0.00, y1: 2.30, sweep: [0, 720] }),
    damage: 50, poise: 52, knock: 4.2, lift: 0.8, launch: true, radial: 1,
    drag: 3.4, hitstop: 'heavy', shake: 0.68, kick: 0.5, hitCap: 40,
    root: { d: 0.95, a: 10, b: 42, ease: 'outCubic' },
    charge: 'ch5', vfx: 'slashSpin', sfx: 'swing_huge', voice: 'shout', tags: 'sweep360',
  }),
  mv({
    id: 'ch5', label: 'Lance Charge', clip: 'spear_ch5_couch', rate: 0.98,
    windup: 42, active: 16, recovery: 20,
    arc: A({ half: 40, r0: 0.30, r1: 5.20, y0: -0.05, y1: 2.40, sweep: [0, 0] }),
    damage: 96, poise: 90, knock: 12.0, lift: 1.6, launch: true, gb: true, radial: 0.1,
    drag: 2.5, hitstop: 'huge', shake: 1.0, kick: 1.0, hitCap: 14,
    root: { d: 3.60, a: 24, b: 70, ease: 'outQuint' },
    vfx: 'pierce', sfx: 'swing_huge', voice: 'shout', tags: 'wallSlam',
  }),

  mv({
    id: 'dash', label: 'Charging Point', clip: 'spear_skewer', rate: 1.25,
    windup: 20, active: 9, recovery: 8,
    arc: A({ half: 40, r0: 0.40, r1: 4.10, y0: 0.10, y1: 2.10 }),
    damage: 42, poise: 28, knock: 2.6, lift: 0.5, radial: 0.15, hitstop: 'med', shake: 0.38,
    root: { d: 3.80, a: 6, b: 30, ease: 'outQuart' }, hitCap: 8,
    next: 'c2', charge: 'ch3', vfx: 'pierce', sfx: 'swing_heavy',
  }),
  mv({
    id: 'air1', label: 'Air Jab', clip: 'spear_thrust_1', rate: 1.40, airborne: true,
    windup: 6, active: 4, recovery: 12,
    arc: A({ half: 40, r0: 0.60, r1: 3.30, y0: -1.60, y1: 1.50 }),
    damage: 30, poise: 14, knock: 1.0, lift: 0.5, radial: 0.4, hitstop: 'light',
    root: { d: 0.50, a: 3, b: 12, ease: 'outQuint' }, hitCap: 8,
    next: 'air2', charge: 'airSlam', vfx: 'pierce', sfx: 'swing_light',
  }),
  mv({
    id: 'air2', label: 'Air Sweep', clip: 'spear_thrust_2', rate: 1.45, airborne: true,
    windup: 6, active: 5, recovery: 11,
    arc: A({ half: 96, r0: 0.40, r1: 3.30, y0: -1.80, y1: 1.40 }),
    damage: 36, poise: 20, knock: 1.4, lift: 0.6, radial: 0.6, hitstop: 'light',
    root: { d: 0.50, a: 3, b: 13, ease: 'outQuint' },
    charge: 'airSlam', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'airSlam', label: 'Spike Drop', clip: 'spear_ch3_quake', rate: 1.49, airborne: true, dive: true,
    windup: 20, active: 6, recovery: 15,
    arc: A({ half: 180, r0: 0.00, r1: 3.40, y0: -2.60, y1: 1.20 }),
    damage: 72, poise: 66, knock: 2.8, lift: 1.0, launch: true, gb: true, radial: 1,
    drag: 3.5, hitstop: 'huge', shake: 0.85, kick: 0.65, hitCap: 30,
    root: { d: 0.35, a: 10, b: 32, ease: 'outQuad' },
    vfx: 'groundSlam', sfx: 'impact_slam', voice: 'shout', tags: 'aoe',
  }),
  mv({
    id: 'musouFinish', label: 'Serpent Ascends', clip: 'musou_finish', rate: 0.95,
    windup: 35, active: 16, recovery: 19, ownWindows: true,   // clip t 0.48 at rate 0.95
    arc: A({ half: 180, r0: 0.00, r1: 7.00, y0: -0.40, y1: 3.40 }),
    damage: 210, poise: 300, knock: 8.5, lift: 3.4, launch: true, gb: true, radial: 1,
    drag: 2.8, hitstop: 'musou', shake: 1.2, kick: 1.2, hitCap: 48,
    root: { d: 2.20, a: 18, b: 44, ease: 'outQuint' },
    extra: [{ at: 35, dur: 4 }, { at: 43, dur: 4, arc: A({ half: 180, r0: 2.6, r1: 10.5, y0: -0.40, y1: 3.40 }), damage: 130, knock: 6.5, lift: 2.4 }],
    vfx: 'musouFinish', presentation: PRESENTATION.COMBAT_VFX | PRESENTATION.MUSOU_FINISHER,
    sfx: 'impact_musou', voice: 'shout', tags: 'musou',
  }),
]);

/* =========================================================================
 *  TWIN SABRES — short reach, very fast, wide arcs, low damage per hit but a
 *  seven-hit chain. Proves the table generalises in the other direction from
 *  the spear: everything is smaller and quicker.
 * ========================================================================= */

const SABRES = table([
  mv({
    id: 'c1', label: 'Left Slash', clip: 'sabre_cross_1', rate: 1.60,
    windup: 6, active: 4, recovery: 8,
    arc: A({ half: 74, bias: -12, r0: 0.20, r1: 2.05, y0: 0.20, y1: 1.90, sweep: [70, -80] }),
    damage: 22, poise: 8, knock: 1.1, hitstop: 'light', shake: 0.18,
    root: { d: 0.65, a: 3, b: 11, ease: 'outQuint' },
    next: 'c2', charge: 'ch2', vfx: 'slash', sfx: 'swing_fast',
  }),
  mv({
    id: 'c2', label: 'Right Slash', clip: 'sabre_cross_2', rate: 1.60,
    windup: 5, active: 4, recovery: 8,
    arc: A({ half: 76, bias: 12, r0: 0.20, r1: 2.05, y0: 0.15, y1: 1.85, sweep: [-72, 82] }),
    damage: 22, poise: 8, knock: 1.1, hitstop: 'light', shake: 0.18,
    root: { d: 0.60, a: 3, b: 10, ease: 'outQuint' },
    next: 'c3', charge: 'ch3', vfx: 'slash', sfx: 'swing_fast',
  }),
  mv({
    id: 'c3', label: 'Cross Cut', clip: 'sabre_x_cut', rate: 1.55,
    windup: 6, active: 5, recovery: 10,
    arc: A({ half: 88, r0: 0.20, r1: 2.20, y0: 0.20, y1: 2.10, sweep: [90, -90] }),
    damage: 26, poise: 12, knock: 1.4, hitstop: 'light', shake: 0.22,
    root: { d: 0.75, a: 4, b: 13, ease: 'outQuint' },
    next: 'c4', charge: 'ch4', vfx: 'slash', sfx: 'swing_fast',
  }),
  mv({
    id: 'c4', label: 'Twin Cyclone', clip: 'sabre_cyclone', rate: 1.35,
    windup: 11, active: 19, recovery: 11,
    arc: A({ half: 180, r0: 0.20, r1: 2.70, y0: 0.00, y1: 2.10, sweep: [0, 360] }),
    damage: 34, poise: 26, knock: 2.6, lift: 1.05, launch: true, radial: 1,
    drag: 4.0, hitstop: 'med', shake: 0.44, kick: 0.4, hitCap: 30,
    root: { d: 1.10, a: 7, b: 26, ease: 'outCubic' },
    next: 'c5', charge: 'ch5', vfx: 'slashSpin', sfx: 'swing_heavy',
  }),
  mv({
    id: 'c5', label: 'Rushing Blades', clip: 'sabre_rush', rate: 1.05,
    windup: 8, active: 6, recovery: 11,
    arc: A({ half: 96, r0: 0.20, r1: 2.40, y0: 0.10, y1: 2.10, sweep: [100, -100] }),
    damage: 38, poise: 24, knock: 1.8, hitstop: 'med', shake: 0.3,
    root: { d: 0.95, a: 4, b: 16, ease: 'outQuint' },
    next: 'c6', charge: 'ch5', vfx: 'slashBig', sfx: 'swing_heavy',
  }),
  mv({
    id: 'c6', label: 'Scissor Finish', clip: 'sabre_c6_scissor', rate: 1.12,
    // 15 = the clip's hitboxOn at t 0.40 and rate 1.12; the scissor shuts at 22.
    windup: 15, active: 7, recovery: 16,
    arc: A({ half: 130, r0: 0.15, r1: 2.80, y0: -0.10, y1: 2.20, sweep: [-130, 130] }),
    damage: 72, poise: 70, knock: 4.4, lift: 1.9, launch: true, gb: true, radial: 0.85,
    drag: 3.3, hitstop: 'huge', shake: 0.75, kick: 0.7, hitCap: 30,
    root: { d: 1.50, a: 10, b: 30, ease: 'outQuint' },
    charge: 'ch5', vfx: 'slashHuge', sfx: 'swing_huge', voice: 'shout',
  }),

  mv({
    id: 'ch1', label: 'Uppercut Pair', clip: 'sabre_ch1_uppair', rate: 1.28,
    windup: 10, active: 7, recovery: 13,
    arc: A({ half: 82, r0: 0.15, r1: 2.35, y0: -0.20, y1: 2.80, sweep: [86, -86] }),
    damage: 34, poise: 42, knock: 0.8, lift: 2.90, launch: true, radial: 0.35,
    drag: 4.8, hitstop: 'med', shake: 0.36,
    root: { d: 0.80, a: 5, b: 18, ease: 'outQuint' },
    charge: 'ch2', vfx: 'launchArc', sfx: 'swing_heavy', tags: 'launcher',
  }),
  mv({
    id: 'ch2', label: 'Pommel Rap', clip: 'sabre_ch2_pommel', rate: 1.33,
    windup: 8, active: 6, recovery: 14,
    arc: A({ half: 78, r0: 0.15, r1: 2.10, y0: 0.30, y1: 2.00 }),
    damage: 26, poise: 58, knock: 0.2, stun: 1.45, radial: 0.7,
    hitstop: 'med', shake: 0.32,
    root: { d: 0.55, a: 4, b: 15, ease: 'outQuint' },
    charge: 'ch3', vfx: 'stunBurst', sfx: 'impact_blunt', tags: 'stun',
  }),
  mv({
    id: 'ch3', label: 'Kneeling Slam', clip: 'sabre_ch3_kneel', rate: 1.20,
    windup: 24, active: 8, recovery: 19,
    arc: A({ half: 180, r0: 0.00, r1: 3.90, y0: -0.30, y1: 1.60, sweep: [0, 360] }),
    damage: 50, poise: 66, knock: 3.0, lift: 1.25, launch: true, gb: true, radial: 1,
    drag: 3.6, hitstop: 'heavy', shake: 0.8, kick: 0.6, hitCap: 30,
    root: { d: 0.90, a: 11, b: 26, ease: 'outQuart' },
    extra: [{ at: 40, dur: 5, arc: A({ half: 180, r0: 2.6, r1: 5.8, y0: -0.30, y1: 1.40 }),
      damage: 24, poise: 22, knock: 2.2, lift: 0.6, radial: 1, hitstop: 'none', vfx: 'shock' }],
    charge: 'ch4', vfx: 'groundSlam', sfx: 'impact_slam', tags: 'aoe',
  }),
  mv({
    id: 'ch4', label: 'Blade Storm', clip: 'sabre_ch4_storm', rate: 1.14,
    windup: 13, active: 25, recovery: 14,
    arc: A({ half: 180, r0: 0.15, r1: 3.60, y0: 0.00, y1: 2.20, sweep: [0, 720] }),
    damage: 40, poise: 46, knock: 3.8, lift: 0.75, launch: true, radial: 1,
    drag: 3.6, hitstop: 'med', shake: 0.6, kick: 0.45, hitCap: 40,
    root: { d: 0.85, a: 9, b: 38, ease: 'outCubic' },
    charge: 'ch5', vfx: 'slashSpin', sfx: 'swing_heavy', tags: 'sweep360',
  }),
  mv({
    id: 'ch5', label: 'Shoulder Rush', clip: 'sabre_ch5_barge', rate: 1.15,
    windup: 34, active: 14, recovery: 18,
    arc: A({ half: 62, r0: 0.15, r1: 3.40, y0: -0.05, y1: 2.30 }),
    damage: 86, poise: 88, knock: 10.0, lift: 1.7, launch: true, gb: true, radial: 0.15,
    drag: 2.7, hitstop: 'huge', shake: 0.95, kick: 0.95,
    root: { d: 2.80, a: 18, b: 48, ease: 'outQuint' },
    vfx: 'pierce', sfx: 'swing_huge', voice: 'shout', tags: 'wallSlam',
  }),

  mv({
    id: 'dash', label: 'Sliding Cut', clip: 'sabre_ch5_barge', rate: 2.24,
    windup: 18, active: 8, recovery: 8,
    arc: A({ half: 76, r0: 0.15, r1: 2.60, y0: 0.00, y1: 2.00 }),
    damage: 34, poise: 24, knock: 2.2, lift: 0.45, radial: 0.35, hitstop: 'light', shake: 0.32,
    root: { d: 3.20, a: 5, b: 26, ease: 'outQuart' },
    next: 'c2', charge: 'ch3', vfx: 'slash', sfx: 'swing_fast',
  }),
  mv({
    id: 'air1', label: 'Air Cross', clip: 'sabre_cross_1', rate: 1.60, airborne: true,
    windup: 5, active: 4, recovery: 10,
    arc: A({ half: 82, r0: 0.15, r1: 2.10, y0: -1.50, y1: 1.50 }),
    damage: 26, poise: 12, knock: 1.0, lift: 0.5, radial: 0.5, hitstop: 'light',
    root: { d: 0.45, a: 3, b: 10, ease: 'outQuint' },
    next: 'air2', charge: 'airSlam', vfx: 'slash', sfx: 'swing_fast',
  }),
  mv({
    id: 'air2', label: 'Air Scissor', clip: 'sabre_cross_2', rate: 1.55, airborne: true,
    windup: 5, active: 4, recovery: 11,
    arc: A({ half: 90, r0: 0.15, r1: 2.20, y0: -1.70, y1: 1.40 }),
    damage: 30, poise: 16, knock: 1.2, lift: 0.6, radial: 0.6, hitstop: 'light',
    root: { d: 0.45, a: 3, b: 11, ease: 'outQuint' },
    charge: 'airSlam', vfx: 'slash', sfx: 'swing_fast',
  }),
  mv({
    id: 'airSlam', label: 'Falling Star', clip: 'sabre_ch3_kneel', rate: 1.60, airborne: true, dive: true,
    windup: 19, active: 5, recovery: 14,
    arc: A({ half: 180, r0: 0.00, r1: 3.10, y0: -2.50, y1: 1.20 }),
    damage: 62, poise: 60, knock: 2.6, lift: 0.95, launch: true, gb: true, radial: 1,
    drag: 3.6, hitstop: 'heavy', shake: 0.8, kick: 0.6, hitCap: 28,
    root: { d: 0.30, a: 9, b: 30, ease: 'outQuad' },
    vfx: 'groundSlam', sfx: 'impact_slam', tags: 'aoe',
  }),
  mv({
    id: 'musouFinish', label: 'Thousand Cuts', clip: 'musou_finish', rate: 1.00,
    windup: 33, active: 14, recovery: 15, ownWindows: true,   // clip t 0.48 at rate 1.00
    arc: A({ half: 180, r0: 0.00, r1: 6.20, y0: -0.40, y1: 3.00 }),
    damage: 180, poise: 300, knock: 8.0, lift: 3.0, launch: true, gb: true, radial: 1,
    drag: 2.9, hitstop: 'musou', shake: 1.15, kick: 1.15, hitCap: 48,
    root: { d: 1.40, a: 16, b: 40, ease: 'outQuint' },
    extra: [{ at: 33, dur: 4 }, { at: 40, dur: 4, arc: A({ half: 180, r0: 2.2, r1: 9.0, y0: -0.40, y1: 3.00 }), damage: 110, knock: 6.0, lift: 2.2 }],
    vfx: 'musouFinish', presentation: PRESENTATION.COMBAT_VFX | PRESENTATION.MUSOU_FINISHER,
    sfx: 'impact_musou', voice: 'shout', tags: 'musou',
  }),
]);

/* =========================================================================
 *  MUSOU  — meter-gated, invulnerable, 3-6 s, a multi-hit 360 flurry that
 *  ends in a finisher. The flurry is PULSED rather than event-driven: the
 *  spin clip loops and combat fires one 360 arc every `pulse.every` frames,
 *  which is exactly how the real thing reads (a continuous grinder that keeps
 *  bodies in the air) and lets the duration be data instead of animation.
 * ========================================================================= */

const musou = (o) => Object.assign({
  clip: 'musou_grind', rate: 1.00,
  duration: 3.6,          // seconds of flurry before the finisher
  cost: 1.0,              // fraction of the gauge required AND spent
  moveSpeed: 3.4,         // the hero drifts while grinding
  // `musou_grind` turns the HIPS exactly 360 deg per 0.72 s cycle, i.e.
  // 500*rate deg/s of body rotation, and src/game/player.js yaws the ENTITY by
  // `spin` on top of that. The two add: 500 + 140 = 640 deg/s here, 1.8
  // revolutions a second. (It used to be attack_light_4 at rate 1.25 — 557
  // deg/s of clip — plus spin 520, so the hero span at 1077 deg/s.)
  spin: 140,              // degrees/second of ENTITY yaw ON TOP of the clip
  finish: 'musouFinish',
  pulse: {
    every: 7,             // frames between hits
    arc: A({ half: 180, r0: 0.10, r1: 4.20, y0: -0.20, y1: 2.60 }),
    damage: 30, poise: 16, knock: 1.1, lift: 0.55, drag: 4.6, radial: 1,
    hitstop: 'none', shake: 0.14, hitCap: 40, vfx: 'musouHit',
    presentation: PRESENTATION.COMBAT_VFX | PRESENTATION.QUIET_IMPACT, sfx: 'impact_musou',
  },
}, o);

/* ------------------------------------------------------------------ export */

/** Non-attack states that every weapon shares. */
export const COMMON = {
  dodge: { clip: 'dodge_roll', rate: 1.15, dur: 0.66, invuln: 0.34, dist: 4.2, cancelAt: 0.44 },
  dash: { clip: 'dash', rate: 1.0 },
  guard: { clip: 'block', impact: 'block_impact', chip: 0.16, breakClip: 'guard_break' },
  jump: { up: 7.4, gravity: -21, maxAir: 2 },
  hurt: { light: 'hit_light', heavy: 'hit_heavy', launch: 'launch', air: 'airborne', down: 'knockdown', getup: 'getup' },
};

/* =========================================================================
 *  GADA — the heavy blunt weapon (DEV_PLAN Step 8, weapon 1 of 3)
 * -------------------------------------------------------------------------
 *  Three weapons shipped before this one and, measured with
 *  tools/clip-shape.mjs, they occupy one corner of the space: swept boxes that
 *  are Z-dominant, tips that stay above 1.0 m on almost every move, and power
 *  that comes from turning the hips — 455 deg on the glaive's Whirling Moon,
 *  798 on the spear's Dragon Tail. This one is built on the axes nobody used:
 *  VERTICAL (pitch, not yaw), LOW (three moves put the head at or under knee
 *  height), and SHORT (the head is 1.05 m from the grip against the glaive's
 *  1.78).
 *
 *  THE PRICE OF ALL THAT IS TIME, and it is charged here rather than implied.
 *  Every windup is longer than its opposite number on the glaive and every
 *  recovery is much longer: c1 is 14/7/20 frames against the glaive's 9/6/12.
 *  A mace that recovered like a sabre would be strictly better than one and the
 *  moveset would have nothing to say. What it buys is poise and guard-break —
 *  five of the eleven moves break a guard, where the glaive has two — so the
 *  gada's answer to a defended man is not to go around the guard but through
 *  it.
 *
 *  hitCap is HIGH throughout for the same reason: this weapon's whole argument
 *  is that it does not care how many men are in the way.
 * ========================================================================= */

/* THE FRAME NUMBERS BELOW ARE THE CLIPS' OWN MARKERS, not estimates.
 *
 * The first version of this table guessed them, and combat.js's own validator
 * had been printing the result into the browser console since the day it
 * shipped: ten `moveset drift` warnings, the worst of them `gada.c6: windup 26f
 * vs marker 36.2f`. The hitbox opened ten frames before the animation swung,
 * so the gada dealt its damage before the head moved. I never read that log —
 * it took a capture's stats.json to put it in front of me.
 *
 * windup = the clip's hitboxOn frame at this rate; active = off minus on;
 * recovery = the clip's length minus off. Retime a clip and these must move
 * with it; `node tools/moveset-drift.mjs` reports any that have not.
 */
const GADA = table([
  /* --- light chain C1..C6 : lift and drop, escalating --------------------- */
  mv({
    id: 'c1', label: 'Overhead Drop', clip: 'gada_c1', rate: 1.00,
    windup: 15, active: 5, recovery: 17,
    arc: A({ half: 46, bias: 0, r0: 0.30, r1: 2.30, y0: -0.05, y1: 2.20, sweep: [0, 0] }),
    damage: 44, poise: 34, knock: 1.9, hitstop: 'med', shake: 0.40, kick: 0.3, hitCap: 10,
    root: { d: 0.55, a: 6, b: 20, ease: 'outQuint' },
    next: 'c2', charge: 'ch2', vfx: 'slash', sfx: 'swing_heavy',
  }),
  /* C2 — the low one. Nothing else in the game swings at ankle height, so this
   * is the move that catches a man who has just rolled and the move that goes
   * under a raised guard. */
  mv({
    id: 'c2', label: 'Shin Sweep', clip: 'gada_c2', rate: 1.05,
    windup: 11, active: 6, recovery: 16,
    arc: A({ half: 120, bias: 6, r0: 0.35, r1: 2.45, y0: -0.15, y1: 1.05, sweep: [-116, 104] }),
    damage: 38, poise: 28, knock: 2.2, lift: 0.45, launch: true, radial: 0.7,
    hitstop: 'light', shake: 0.32, hitCap: 20,
    root: { d: 0.50, a: 5, b: 18, ease: 'outQuint' },
    next: 'c3', charge: 'ch3', vfx: 'slash', sfx: 'swing_heavy',
  }),
  mv({
    id: 'c3', label: 'Rising Crown', clip: 'gada_c3', rate: 1.00,
    windup: 14, active: 7, recovery: 18,
    arc: A({ half: 60, bias: -8, r0: 0.30, r1: 2.40, y0: 0.10, y1: 2.55, sweep: [40, -30] }),
    damage: 46, poise: 36, knock: 1.8, lift: 1.30, launch: true,
    hitstop: 'med', shake: 0.38, hitCap: 12,
    root: { d: 0.70, a: 5, b: 19, ease: 'outQuint' },
    next: 'c4', charge: 'ch4', vfx: 'slashBig', sfx: 'swing_heavy',
  }),
  mv({
    id: 'c4', label: 'Waist Turn', clip: 'gada_c4', rate: 1.00,
    windup: 17, active: 10, recovery: 18,
    arc: A({ half: 150, bias: 0, r0: 0.35, r1: 2.55, y0: 0.15, y1: 2.30, sweep: [-146, 140] }),
    damage: 50, poise: 42, knock: 2.6, gb: true, radial: 0.8,
    hitstop: 'med', shake: 0.44, kick: 0.32, hitCap: 24,
    root: { d: 0.75, a: 6, b: 22, ease: 'outQuint' },
    next: 'c5', charge: 'ch5', vfx: 'slashBig', sfx: 'swing_heavy',
  }),
  mv({
    id: 'c5', label: 'Stepping Smash', clip: 'gada_c5', rate: 1.00,
    windup: 26, active: 9, recovery: 19,
    arc: A({ half: 56, bias: 0, r0: 0.30, r1: 2.70, y0: -0.10, y1: 2.15, sweep: [0, 0] }),
    damage: 62, poise: 58, knock: 3.2, gb: true, drag: 3.4,
    hitstop: 'heavy', shake: 0.60, kick: 0.45, hitCap: 14,
    root: { d: 1.25, a: 8, b: 26, ease: 'outQuint' },
    next: 'c6', charge: 'ch3', vfx: 'groundSlam', sfx: 'impact_slam', voice: 'shout',
  }),
  /* C6 — the finisher, and the slowest light-chain move in the game at 34
   * frames of recovery. It is a ring, not an arc: the head goes into the dirt
   * and everything standing round the crater goes up. */
  mv({
    id: 'c6', label: 'Earth Shatter', clip: 'gada_c6', rate: 1.00,
    windup: 36, active: 13, recovery: 21,
    arc: A({ half: 180, bias: 0, r0: 0.00, r1: 3.60, y0: -0.30, y1: 1.70, sweep: [0, 360] }),
    damage: 78, poise: 90, knock: 3.8, lift: 1.40, launch: true, gb: true, radial: 1,
    drag: 3.0, hitstop: 'huge', shake: 0.82, kick: 0.6, hitCap: 32,
    root: { d: 1.00, a: 10, b: 30, ease: 'outQuad' },
    charge: 'ch3', vfx: 'groundSlam', sfx: 'impact_slam', voice: 'shout', tags: 'aoe',
  }),

  /* --- charge branches ---------------------------------------------------- */
  mv({
    id: 'ch1', label: 'Skyward Lift', clip: 'gada_ch1', rate: 1.00,
    windup: 23, active: 9, recovery: 20,
    arc: A({ half: 70, bias: -6, r0: 0.25, r1: 2.50, y0: -0.20, y1: 3.00, sweep: [56, -40] }),
    damage: 54, poise: 52, knock: 1.0, lift: 3.20, launch: true, radial: 0.35,
    drag: 4.6, hitstop: 'heavy', shake: 0.46, kick: 0.35, hitCap: 14,
    root: { d: 0.75, a: 7, b: 22, ease: 'outQuint' },
    charge: 'ch2', vfx: 'launchArc', sfx: 'swing_heavy', voice: 'shout', tags: 'launcher',
  }),
  /* ch2 — STUN, and the set's one fast move. A butt-end punch: 10 frames of
   * windup where everything else here takes fourteen or more, which is what
   * makes it the answer when the chain has left you standing in the open. */
  mv({
    id: 'ch2', label: 'Haft Punch', clip: 'gada_ch2', rate: 1.00,
    windup: 10, active: 4, recovery: 17,
    arc: A({ half: 34, bias: 0, r0: 0.30, r1: 1.90, y0: 0.30, y1: 2.00, sweep: [0, 0] }),
    damage: 34, poise: 66, knock: 0.25, stun: 1.70, radial: 0.7,
    hitstop: 'med', shake: 0.34, kick: 0.26, hitCap: 6,
    root: { d: 0.50, a: 4, b: 15, ease: 'outQuint' },
    charge: 'ch3', vfx: 'spark', sfx: 'impact_blunt', tags: 'stun',
  }),
  /* ch3 — the crater. The widest ring in the set and the longest recovery of
   * any charge on any weapon: 38 frames where the glaive's slam takes 24. */
  mv({
    id: 'ch3', label: 'Quake', clip: 'gada_ch3', rate: 1.00,
    windup: 33, active: 12, recovery: 21,
    arc: A({ half: 180, bias: 0, r0: 0.00, r1: 5.60, y0: -0.35, y1: 1.60, sweep: [0, 360] }),
    damage: 88, poise: 120, knock: 4.4, lift: 1.60, launch: true, gb: true, radial: 1,
    drag: 2.9, hitstop: 'huge', shake: 1.0, kick: 0.75, hitCap: 40,
    root: { d: 0.90, a: 12, b: 32, ease: 'outQuad' },
    charge: 'ch4', vfx: 'groundSlam', sfx: 'impact_slam', voice: 'shout', tags: 'aoe',
  }),
  /* ch4 — the one spin, and it spins LOW. Two turns with the head at hip
   * height, so it goes under a guard the whole way round. */
  mv({
    id: 'ch4', label: 'Grinding Wheel', clip: 'gada_ch4', rate: 1.00,
    windup: 25, active: 39, recovery: 10,
    arc: A({ half: 180, bias: 0, r0: 0.20, r1: 2.95, y0: -0.10, y1: 1.45, sweep: [0, 720] }),
    damage: 40, poise: 48, knock: 2.4, gb: true, radial: 0.9,
    drag: 4.0, hitstop: 'light', shake: 0.40, kick: 0.3, hitCap: 44,
    root: { d: 1.60, a: 10, b: 66, ease: 'outQuad' },
    charge: 'ch5', vfx: 'slashBig', sfx: 'swing_heavy', voice: 'shout', tags: 'sweep360',
  }),
  /* ch5 — the travel move, and the only one that ends higher than it starts:
   * carry the head low through a rank of men, then throw it up through them. */
  mv({
    id: 'ch5', label: 'Bull Rush', clip: 'gada_ch5', rate: 1.00,
    windup: 22, active: 39, recovery: 11,
    arc: A({ half: 70, bias: 0, r0: 0.25, r1: 2.60, y0: -0.10, y1: 1.60, sweep: [0, 0] }),
    damage: 44, poise: 70, knock: 2.0, radial: 0.3, drag: 3.6,
    hitstop: 'med', shake: 0.42, kick: 0.4, hitCap: 20,
    root: { d: 3.00, a: 8, b: 42, ease: 'outQuart' },
    extra: [{ at: 40, dur: 8, arc: A({ half: 90, r0: 0.25, r1: 2.80, y0: 0.20, y1: 2.90 }),
      damage: 70, knock: 3.4, lift: 2.6, launch: true, gb: true }],
    vfx: 'slashBig', sfx: 'swing_heavy', voice: 'shout', tags: 'wallSlam',
  }),

  /* --- dash / air / musou ------------------------------------------------- */
  mv({
    id: 'dash', label: 'Running Smash', clip: 'gada_ch5', rate: 1.45,
    windup: 14, active: 8, recovery: 12,
    arc: A({ half: 60, r0: 0.25, r1: 2.60, y0: 0.00, y1: 1.90, sweep: [0, 0] }),
    damage: 48, poise: 44, knock: 3.0, lift: 0.5, radial: 0.25,
    hitstop: 'med', shake: 0.42, kick: 0.45, hitCap: 12,
    root: { d: 2.60, a: 5, b: 26, ease: 'outQuart' },
    next: 'c2', charge: 'ch3', vfx: 'slash', sfx: 'swing_heavy',
  }),
  mv({
    id: 'air1', label: 'Falling Head', clip: 'gada_c1', rate: 1.25, airborne: true,
    // 12 = gada_c1's hitboxOn at rate 1.25. Was 9, three frames before the head moved.
    windup: 12, active: 4, recovery: 14,
    arc: A({ half: 50, bias: 0, r0: 0.30, r1: 2.20, y0: -1.70, y1: 1.50, sweep: [0, 0] }),
    damage: 46, poise: 30, knock: 1.6, lift: 0.45, radial: 0.5,
    hitstop: 'med', shake: 0.32, hitCap: 8,
    root: { d: 0.45, a: 4, b: 15, ease: 'outQuint' },
    next: 'air2', charge: 'airSlam', vfx: 'slash', sfx: 'swing_heavy',
  }),
  mv({
    id: 'air2', label: 'Falling Sweep', clip: 'gada_c2', rate: 1.20, airborne: true,
    windup: 9, active: 8, recovery: 17,
    arc: A({ half: 120, bias: 0, r0: 0.30, r1: 2.40, y0: -1.80, y1: 1.10, sweep: [-110, 100] }),
    damage: 44, poise: 30, knock: 2.0, lift: 0.5, radial: 0.6,
    hitstop: 'light', shake: 0.30, hitCap: 14,
    root: { d: 0.45, a: 4, b: 16, ease: 'outQuint' },
    charge: 'airSlam', vfx: 'slash', sfx: 'swing_heavy',
  }),
  mv({
    id: 'airSlam', label: 'Falling Mountain', clip: 'gada_ch3', rate: 1.30, airborne: true, dive: true,
    // 25 = gada_ch3's hitboxOn at rate 1.30. A dive deliberately ends early, so
    // the length is a slice and only the opening frame is pinned.
    windup: 25, active: 9, recovery: 16,
    arc: A({ half: 180, r0: 0.00, r1: 4.20, y0: -2.60, y1: 1.20, sweep: [0, 360] }),
    damage: 96, poise: 90, knock: 3.4, lift: 1.20, launch: true, gb: true, radial: 1,
    drag: 3.2, hitstop: 'huge', shake: 1.0, kick: 0.8, hitCap: 36,
    root: { d: 0.35, a: 10, b: 34, ease: 'outQuad' },
    vfx: 'groundSlam', sfx: 'impact_slam', voice: 'shout', tags: 'aoe',
  }),
  mv({
    id: 'musouFinish', label: 'Mountain Falls on Water', clip: 'gada_musou', rate: 0.86,
    /* 45 = the frame `gada_musou` plants the head at rate 0.86:
     *   t 0.50 x dur 1.30 x 60 / 0.86 = 45.3.
     * ownWindows, so these are pinned by hand — retime them if the clip moves.
     * It was 39 against the shared `musou_finish`; authoring the gada its own
     * finisher moved the impact and this had to move with it. */
    windup: 45, active: 17, recovery: 26, ownWindows: true,
    arc: A({ half: 180, r0: 0.00, r1: 6.80, y0: -0.40, y1: 2.60, sweep: [0, 360] }),
    damage: 250, poise: 320, knock: 9.6, lift: 2.60, launch: true, gb: true, radial: 1,
    drag: 2.6, hitstop: 'musou', shake: 1.3, kick: 1.3, hitCap: 52,
    root: { d: 1.20, a: 20, b: 50, ease: 'outQuint' },
    extra: [
      { at: 45, dur: 4 },
      { at: 53, dur: 5, arc: A({ half: 180, r0: 2.4, r1: 10.0, y0: -0.40, y1: 2.20 }), damage: 150, knock: 7.4, lift: 1.6 },
    ],
    vfx: 'groundSlam', sfx: 'impact_slam', voice: 'shout', tags: 'aoe',
  }),
]);

/* =========================================================================
 *  CAMBUK NAGA — the chain whip (DEV_PLAN Step 8, weapon 2 of 3)
 * -------------------------------------------------------------------------
 *  THE REACH WEAPON THAT IS NOT THE SPEAR, and what makes it not the spear is
 *  `r0`. Every other weapon in this file opens its arcs at r0 0.25-0.45 — the
 *  blade is dangerous from the wielder's own shins outward. This one starts at
 *  1.10 and up: the naga head is on the end of 1.6 m of chain and it CANNOT
 *  hurt anything standing inside that. Measured across the four weapons before
 *  it, each brings its business end within 0.09 m of the wielder's axis; this
 *  is the first one whose damage lives out at the rim.
 *
 *  So the trade is not "long reach, less damage". It is: nothing else in the
 *  game controls this much ground, and nothing else is helpless against a man
 *  who closes. `ch2` is the whole admission — a jab with the HANDLE, the only
 *  move in the set with a small r0, because when something gets inside the
 *  chain the chain is not the answer.
 *
 *  Numbers follow from that. Damage per hit is the lowest in the game and
 *  hitCap the highest, because a 4 m ring through a crowd should be counted in
 *  bodies rather than in damage. Poise is low and only ch4 breaks a guard: the
 *  weapon controls space, it does not break lines.
 * ========================================================================= */

const CAMBUK = table([
  /* --- light chain C1..C6 : the head orbits, the body turns under it ------ */
  mv({
    id: 'c1', label: 'Outer Circle', clip: 'cambuk_c1', rate: 1.00,
    windup: 13, active: 6, recovery: 16,
    arc: A({ half: 150, bias: 0, r0: 1.15, r1: 3.80, y0: 0.20, y1: 2.30, sweep: [-146, 140] }),
    damage: 22, poise: 8, knock: 1.1, drag: 4.6, radial: 0.85,
    hitstop: 'light', shake: 0.22, kick: 0.18, hitCap: 34,
    root: { d: 0.45, a: 5, b: 18, ease: 'outQuint' },
    next: 'c2', charge: 'ch2', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'c2', label: 'Reverse Circle', clip: 'cambuk_c2', rate: 1.00,
    windup: 11, active: 6, recovery: 16,
    arc: A({ half: 150, bias: 0, r0: 1.15, r1: 3.80, y0: 0.30, y1: 2.50, sweep: [146, -140] }),
    damage: 24, poise: 9, knock: 1.2, drag: 4.6, radial: 0.85,
    hitstop: 'light', shake: 0.24, kick: 0.18, hitCap: 34,
    root: { d: 0.45, a: 5, b: 17, ease: 'outQuint' },
    next: 'c3', charge: 'ch3', vfx: 'slash', sfx: 'swing_light',
  }),
  /* C3 — the low one, and still at the rim: a trip that takes a whole rank
   * off its feet without ever coming inside 1.2 m. */
  mv({
    id: 'c3', label: 'Low Scythe', clip: 'cambuk_c3', rate: 1.00,
    windup: 13, active: 8, recovery: 16,
    arc: A({ half: 160, bias: 0, r0: 1.20, r1: 3.90, y0: -0.10, y1: 1.20, sweep: [-156, 150] }),
    damage: 26, poise: 14, knock: 1.8, lift: 0.55, launch: true, drag: 4.2, radial: 0.9,
    hitstop: 'light', shake: 0.28, hitCap: 36,
    root: { d: 0.55, a: 5, b: 18, ease: 'outQuint' },
    next: 'c4', charge: 'ch4', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'c4', label: 'Overhead Wheel', clip: 'cambuk_c4', rate: 1.00,
    windup: 17, active: 8, recovery: 17,
    arc: A({ half: 80, bias: 0, r0: 1.00, r1: 4.00, y0: 0.00, y1: 2.90, sweep: [0, 0] }),
    damage: 32, poise: 18, knock: 1.9, drag: 4.0, radial: 0.5,
    hitstop: 'med', shake: 0.32, kick: 0.24, hitCap: 18,
    root: { d: 0.60, a: 6, b: 21, ease: 'outQuint' },
    next: 'c5', charge: 'ch5', vfx: 'slash', sfx: 'swing_heavy',
  }),
  /* C5 — two circuits. The crowd-clearer, and the reason hitCap is 44: at
   * 3.9 m through a press of men this is counted in bodies. */
  mv({
    id: 'c5', label: 'Double Orbit', clip: 'cambuk_c5', rate: 1.00,
    windup: 18, active: 31, recovery: 15,
    arc: A({ half: 180, bias: 0, r0: 1.20, r1: 3.90, y0: 0.20, y1: 2.40, sweep: [0, 720] }),
    damage: 24, poise: 12, knock: 1.4, drag: 4.4, radial: 1,
    hitstop: 'light', shake: 0.30, kick: 0.2, hitCap: 44,
    root: { d: 0.85, a: 6, b: 50, ease: 'outQuad' },
    next: 'c6', charge: 'ch3', vfx: 'slashBig', sfx: 'swing_heavy', voice: 'shout', tags: 'aoe',
  }),
  /* C6 — the crack. The chain gathers and snaps out to 4.4 m, the longest
   * single reach in the game, and the one moment this weapon is ever close. */
  mv({
    id: 'c6', label: 'Coil and Crack', clip: 'cambuk_c6', rate: 1.00,
    windup: 30, active: 10, recovery: 22,
    arc: A({ half: 60, bias: 0, r0: 0.80, r1: 4.60, y0: 0.20, y1: 2.60, sweep: [0, 0] }),
    damage: 58, poise: 36, knock: 3.4, lift: 1.5, launch: true, drag: 3.4, radial: 0.35,
    hitstop: 'heavy', shake: 0.58, kick: 0.5, hitCap: 12,
    root: { d: 1.40, a: 10, b: 34, ease: 'outQuart' },
    charge: 'ch4', vfx: 'slashHuge', sfx: 'swing_huge', voice: 'shout',
  }),

  /* --- charge branches ---------------------------------------------------- */
  mv({
    id: 'ch1', label: 'Rising Coil', clip: 'cambuk_ch1', rate: 1.00,
    windup: 21, active: 10, recovery: 19,
    arc: A({ half: 110, bias: 0, r0: 1.10, r1: 4.00, y0: -0.10, y1: 3.10, sweep: [70, -60] }),
    damage: 38, poise: 34, knock: 1.0, lift: 3.00, launch: true, radial: 0.5,
    drag: 4.8, hitstop: 'med', shake: 0.40, kick: 0.28, hitCap: 20,
    root: { d: 0.60, a: 7, b: 24, ease: 'outQuint' },
    charge: 'ch2', vfx: 'launchArc', sfx: 'swing_heavy', voice: 'shout', tags: 'launcher',
  }),
  /* ch2 — THE DEAD ZONE, ADMITTED. A jab with the handle, r0 0.25, for the man
   * who got inside the chain. It is the only move here that can touch him. */
  mv({
    id: 'ch2', label: 'Haft Check', clip: 'cambuk_ch2', rate: 1.00,
    // 9 = cambuk_ch2's hitboxOn. The fastest windup in the game, and it has to
    // be: this is the answer to something already inside the chain.
    windup: 9, active: 3, recovery: 17,
    arc: A({ half: 40, bias: 0, r0: 0.25, r1: 1.70, y0: 0.30, y1: 2.00, sweep: [0, 0] }),
    damage: 26, poise: 58, knock: 0.30, stun: 1.60, radial: 0.7,
    hitstop: 'med', shake: 0.30, kick: 0.22, hitCap: 6,
    root: { d: 0.60, a: 4, b: 16, ease: 'outQuint' },
    charge: 'ch3', vfx: 'spark', sfx: 'impact_blunt', tags: 'stun',
  }),
  /* ch3 — stand still and let them walk into it. The longest active window of
   * any move in the game: 36 frames of open ring at 4.2 m. */
  mv({
    id: 'ch3', label: 'Ring of Coils', clip: 'cambuk_ch3', rate: 1.00,
    // 23/47/8 off the clip's own markers: 47 frames of open ring, the longest
    // active window in the game.
    windup: 23, active: 47, recovery: 8,
    arc: A({ half: 180, bias: 0, r0: 1.30, r1: 4.30, y0: 0.10, y1: 2.30, sweep: [0, 1080] }),
    damage: 26, poise: 16, knock: 1.5, drag: 4.6, radial: 1,
    hitstop: 'light', shake: 0.34, kick: 0.22, hitCap: 52,
    root: { d: 0.80, a: 6, b: 62, ease: 'outQuad' },
    charge: 'ch4', vfx: 'slashBig', sfx: 'swing_heavy', voice: 'shout', tags: 'aoe',
  }),
  /* ch4 — the one that breaks a guard: four metres of chain over the top and
   * down on one point. */
  mv({
    id: 'ch4', label: 'Naga Descends', clip: 'cambuk_ch4', rate: 1.00,
    windup: 34, active: 12, recovery: 20,
    arc: A({ half: 90, bias: 0, r0: 0.90, r1: 4.40, y0: -0.20, y1: 2.60, sweep: [0, 0] }),
    damage: 66, poise: 70, knock: 3.0, lift: 1.7, launch: true, gb: true, radial: 0.5,
    drag: 3.6, hitstop: 'heavy', shake: 0.70, kick: 0.55, hitCap: 16,
    root: { d: 1.00, a: 10, b: 32, ease: 'outQuad' },
    charge: 'ch5', vfx: 'groundSlam', sfx: 'impact_slam', voice: 'shout', tags: 'sweep360',
  }),
  mv({
    id: 'ch5', label: 'Serpent Runs', clip: 'cambuk_ch5', rate: 1.00,
    windup: 16, active: 36, recovery: 18,
    arc: A({ half: 120, bias: 0, r0: 1.10, r1: 4.10, y0: 0.10, y1: 2.20, sweep: [0, 0] }),
    damage: 28, poise: 30, knock: 1.6, radial: 0.6, drag: 4.2,
    hitstop: 'light', shake: 0.34, kick: 0.3, hitCap: 30,
    root: { d: 3.20, a: 8, b: 52, ease: 'outQuart' },
    vfx: 'slashBig', sfx: 'swing_heavy', voice: 'shout', tags: 'wallSlam',
  }),

  /* --- dash / air / musou ------------------------------------------------- */
  mv({
    id: 'dash', label: 'Running Coil', clip: 'cambuk_ch5', rate: 1.50,
    windup: 11, active: 12, recovery: 12,
    arc: A({ half: 110, r0: 1.00, r1: 4.00, y0: 0.10, y1: 2.10, sweep: [0, 0] }),
    damage: 32, poise: 28, knock: 1.8, radial: 0.4,
    hitstop: 'light', shake: 0.32, kick: 0.3, hitCap: 22,
    root: { d: 2.80, a: 5, b: 26, ease: 'outQuart' },
    next: 'c2', charge: 'ch3', vfx: 'slash', sfx: 'swing_heavy',
  }),
  mv({
    id: 'air1', label: 'Falling Circle', clip: 'cambuk_c1', rate: 1.25, airborne: true,
    windup: 10, active: 5, recovery: 13,
    arc: A({ half: 150, bias: 0, r0: 1.00, r1: 3.70, y0: -1.60, y1: 1.60, sweep: [-140, 134] }),
    damage: 26, poise: 10, knock: 1.2, lift: 0.4, radial: 0.8,
    hitstop: 'light', shake: 0.24, hitCap: 26,
    root: { d: 0.40, a: 4, b: 14, ease: 'outQuint' },
    next: 'air2', charge: 'airSlam', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'air2', label: 'Falling Scythe', clip: 'cambuk_c3', rate: 1.20, airborne: true,
    windup: 11, active: 6, recovery: 14,
    arc: A({ half: 160, bias: 0, r0: 1.05, r1: 3.80, y0: -1.80, y1: 1.20, sweep: [-150, 144] }),
    damage: 28, poise: 12, knock: 1.5, lift: 0.5, radial: 0.85,
    hitstop: 'light', shake: 0.26, hitCap: 28,
    root: { d: 0.40, a: 4, b: 15, ease: 'outQuint' },
    charge: 'airSlam', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'airSlam', label: 'Naga Falls', clip: 'cambuk_ch4', rate: 1.30, airborne: true, dive: true,
    windup: 24, active: 9, recovery: 16,
    arc: A({ half: 180, r0: 0.60, r1: 4.40, y0: -2.60, y1: 1.20, sweep: [0, 360] }),
    damage: 74, poise: 70, knock: 2.8, lift: 1.1, launch: true, gb: true, radial: 1,
    drag: 3.6, hitstop: 'huge', shake: 0.85, kick: 0.7, hitCap: 30,
    root: { d: 0.35, a: 10, b: 32, ease: 'outQuad' },
    vfx: 'groundSlam', sfx: 'impact_slam', voice: 'shout', tags: 'aoe',
  }),
  mv({
    id: 'musouFinish', label: 'The Naga Uncoils', clip: 'musou_finish', rate: 0.94,
    // 37 = musou_finish's plant at rate 0.94 (clip t 0.48, dur 1.15).
    windup: 37, active: 16, recovery: 20, ownWindows: true,
    arc: A({ half: 180, r0: 0.60, r1: 9.00, y0: -0.40, y1: 3.00, sweep: [0, 360] }),
    damage: 200, poise: 260, knock: 8.0, lift: 2.40, launch: true, gb: true, radial: 1,
    drag: 3.0, hitstop: 'musou', shake: 1.15, kick: 1.1, hitCap: 60,
    root: { d: 1.50, a: 20, b: 46, ease: 'outQuint' },
    extra: [
      { at: 37, dur: 4 },
      { at: 45, dur: 5, arc: A({ half: 180, r0: 3.0, r1: 13.0, y0: -0.40, y1: 2.60 }), damage: 120, knock: 6.4, lift: 1.4 },
    ],
    vfx: 'slashHuge', sfx: 'swing_huge', voice: 'shout', tags: 'aoe',
  }),
]);

/* =========================================================================
 *  KAPAK GARUDA — paired war axes
 * -------------------------------------------------------------------------
 *  The weapon was chosen by measuring the set, not by picking a theme. Two
 *  gaps came out of that measurement and this fills both.
 *
 *  1. NOTHING IN THIS GAME IS UNBLOCKABLE. `unblockable` has existed in the
 *     move schema since the first moveset shipped and had ZERO users across
 *     all five weapons, while enemies genuinely raise a guard
 *     (ai/grunt.js sets ST.BLOCK). So the guard the player fights against had
 *     no counter that belonged to a weapon rather than to a mechanic.
 *
 *  2. NOTHING IS BOTH SHORT AND HEAVY. The shortest weapon in the game was the
 *     sabres at 1.5 m, and they are nearly the lightest at 0.6. The corner of
 *     the reach-by-weight grid where a weapon has no reach at all and hits
 *     like a sledge was simply empty.
 *
 *  THE CLAIM AS SHIPPED WAS WRONG, AND THIS IS WHAT REPLACED IT.
 *
 *  It originally read: "you are the only thing in the game a raised guard does
 *  not stop." That sentence was written before anything measured it, and the
 *  measurement killed it. Engine counters inside applyHit, over sixty seconds
 *  of live battle, found a raised guard on 6 of 466 landed hits with the
 *  glaive and 11 of 300 with this weapon — about one in fifty. A grunt blocks
 *  on r < 0.09 and, at the rate a player swings, spends most of its life in a
 *  hit reaction instead; it never gets to raise anything. The last hope was
 *  the officer, whose OF.GUARD is his DEFAULT IDLE, and a real duel gave 0 of
 *  10 for this weapon and 3 of 59 for the glaive. An officer who is guarding
 *  is also strafing, so the guard is up but it is not where the blow lands.
 *
 *  There is nowhere in this game where that sentence is worth carrying, so it
 *  is gone. The alternative — raising the AI's block rate until the claim came
 *  true — would have been tuning the game to fit copy already written.
 *
 *  WHAT IS ACTUALLY TRUE, and every line of it is measured:
 *
 *    THE WALL. You must be inside everyone else's range. deadzone_sweep, one
 *    pinned dummy, 0.4-4.6 m in 0.1 m steps, 43/43 valid trials: this weapon's
 *    c1 dies at 2.4 m where the sabres reach 3.1, the gada 3.2 and the cambuk
 *    4.6. The shortest weapon in the game, and the exact inverse of the
 *    cambuk, which owns the rim and nothing inside 1.2 m.
 *
 *    THE RHYTHM. Paired, so the chain alternates and one arm commits while the
 *    other unwinds: c2 opens in 6 frames, tying the fastest opener in the
 *    game, on the lowest chain recovery of any set.
 *
 *    THE PAYOFF FOR GETTING IN. duel_guard_census, one officer of 900 hp,
 *    reproduced exactly across two runs under the seeded RNG:
 *
 *      kapak   9.20 s   14 swings   10 hits   90 hp per hit
 *      gada   12.23 s   19 swings   31 hits   29 hp per hit
 *      glaive 18.82 s   29 swings   59 hits   15 hp per hit
 *
 *    Half the glaive's time on a sixth of the hits — each blow worth about six
 *    of its. That is the trade the wall buys, and it is what this weapon is
 *    FOR: it cannot reach, so every blow it does land has to count, and
 *    against the one enemy with the health to survive an opener it counts more
 *    than anything else in the game. (One officer per weapon, not a
 *    distribution — stated as measured.)
 *
 *  The four `unblockable` finishers STAY. They are honest as a property — the
 *  flag now has readers, and GUARD_SOAK gives a guard something to do for the
 *  one blow in fifty that meets one. They are simply not what the weapon is
 *  for, and the header no longer pretends otherwise.
 *
 *  Paired, so the rhythm is ALTERNATION: right, left, right. Chain recovery is
 *  the lowest in the game because one arm is already committing while the
 *  other unwinds. The weapon is relentless rather than punctuated, which is
 *  what separates it from the gada — the other heavy weapon, but a single one
 *  that must fully rewind between blows.
 *
 *  Every frame window below is pinned to its clip's own hitboxOn marker;
 *  tools/moveset-drift.mjs fails the build otherwise, which it learned to do
 *  after the gada shipped dealing damage ten frames before its head moved.
 * ========================================================================= */
const KAPAK = table([
  /* --- the light chain: right, left, both, hook, drum, stoop ------------- */
  mv({
    id: 'c1', label: 'Right Rend', clip: 'kapak_c1', rate: 1.00,
    windup: 7, active: 4, recovery: 8,
    arc: A({ half: 70, bias: 0, r0: 0.25, r1: 1.60, y0: 0.20, y1: 2.00, sweep: [-52, 44] }),
    damage: 30, poise: 26, knock: 1.0, drag: 3.0, radial: 0.35,
    hitstop: 'light', shake: 0.24, kick: 0.20, hitCap: 8,
    root: { d: 0.40, a: 4, b: 12, ease: 'outQuint' },
    next: 'c2', charge: 'ch2', vfx: 'slash', sfx: 'swing_light',
  }),
  /* C2 — the mirror, and the fastest opener in the game at 6 frames. The left
   * axe starts from a cocked position the right arm's follow-through gave it
   * for free, which is the whole argument for carrying two. */
  mv({
    id: 'c2', label: 'Left Rend', clip: 'kapak_c2', rate: 1.00,
    windup: 6, active: 3, recovery: 7,
    arc: A({ half: 70, bias: 0, r0: 0.25, r1: 1.60, y0: 0.20, y1: 2.00, sweep: [52, -44] }),
    damage: 30, poise: 26, knock: 1.0, drag: 3.0, radial: 0.35,
    hitstop: 'light', shake: 0.24, kick: 0.20, hitCap: 8,
    root: { d: 0.38, a: 4, b: 11, ease: 'outQuint' },
    next: 'c3', charge: 'ch3', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'c3', label: 'Cross Rip', clip: 'kapak_c3', rate: 1.00,
    windup: 9, active: 5, recovery: 9,
    arc: A({ half: 96, bias: 0, r0: 0.25, r1: 1.70, y0: 0.30, y1: 2.10, sweep: [-88, 84] }),
    damage: 38, poise: 34, knock: 1.2, drag: 3.2, radial: 0.5,
    hitstop: 'med', shake: 0.28, kick: 0.22, hitCap: 12,
    root: { d: 0.42, a: 5, b: 14, ease: 'outQuint' },
    next: 'c4', charge: 'ch4', vfx: 'slash', sfx: 'swing_light',
  }),
  /* C4 — the BEAK, not the bit. Negative knock and a heavy drag: it pulls a
   * man toward you instead of away, which on a weapon with no reach is the
   * difference between a hit and a whiff on the next link. */
  mv({
    id: 'c4', label: 'Beak Hook', clip: 'kapak_c4', rate: 1.00,
    windup: 13, active: 5, recovery: 10,
    arc: A({ half: 54, bias: 0, r0: 0.25, r1: 1.75, y0: 0.20, y1: 2.10, sweep: [-30, 26] }),
    damage: 44, poise: 46, knock: -1.4, drag: 5.2, radial: 0.25,
    hitstop: 'heavy', shake: 0.34, kick: 0.26, hitCap: 6,
    root: { d: 0.46, a: 6, b: 16, ease: 'outQuint' },
    next: 'c5', charge: 'ch5', vfx: 'slash', sfx: 'swing_heavy',
  }),
  /* C5 — four blows in one window. 18 active frames on a 46-frame move is the
   * densest light attack in the game. */
  mv({
    id: 'c5', label: 'Twin Drum', clip: 'kapak_c5', rate: 1.00,
    windup: 10, active: 18, recovery: 11,
    arc: A({ half: 80, bias: 0, r0: 0.25, r1: 1.65, y0: 0.20, y1: 2.00, sweep: [-70, 70] }),
    damage: 26, poise: 24, knock: 0.9, drag: 3.0, radial: 0.45,
    hitstop: 'light', shake: 0.22, kick: 0.18, hitCap: 14,
    root: { d: 0.44, a: 5, b: 22, ease: 'outQuint' },
    next: 'c6', charge: 'ch1', vfx: 'slash', sfx: 'swing_light',
  }),
  /* C6 — Garuda Stoop. THE FIRST UNBLOCKABLE MOVE IN THE GAME. A man who
   * raises his guard against this weapon has chosen wrong, and that choice is
   * what the whole set is built to punish. */
  mv({
    id: 'c6', label: 'Garuda Stoop', clip: 'kapak_c6', rate: 1.00,
    windup: 25, active: 10, recovery: 16,
    arc: A({ half: 86, bias: 0, r0: 0.25, r1: 1.85, y0: -0.10, y1: 2.20, sweep: [-60, 60] }),
    damage: 78, poise: 92, knock: 2.2, drag: 3.6, radial: 0.6,
    unblockable: true, gb: true,
    hitstop: 'heavy', shake: 0.46, kick: 0.36, hitCap: 16,
    root: { d: 0.70, a: 8, b: 30, ease: 'outQuint' },
    next: null, charge: null, vfx: 'slam', sfx: 'impact_slam',
  }),

  /* --- charges ---------------------------------------------------------- */
  mv({
    id: 'ch1', label: 'Rising Talon', clip: 'kapak_ch1', rate: 1.00,
    windup: 16, active: 6, recovery: 15,
    arc: A({ half: 72, bias: -4, r0: 0.25, r1: 1.70, y0: -0.10, y1: 2.90, sweep: [40, -30] }),
    damage: 48, poise: 54, knock: 1.1, lift: 3.1, launch: true, drag: 3.4, radial: 0.4,
    hitstop: 'heavy', shake: 0.42, kick: 0.32, hitCap: 10,
    root: { d: 0.55, a: 6, b: 20, ease: 'outQuint' },
    charge: 'ch2', vfx: 'launchArc', sfx: 'launcher', tags: 'launcher',
  }),
  /* CH2 — Shield Split. Slow enough to be read and answered, unblockable so
   * that answering it by guarding does not work. 32 frames of windup is the
   * longest telegraph on the weapon: it is a bet, not a default. */
  mv({
    id: 'ch2', label: 'Shield Split', clip: 'kapak_ch2', rate: 1.00,
    windup: 32, active: 10, recovery: 18,
    arc: A({ half: 48, bias: 0, r0: 0.25, r1: 1.75, y0: 0.00, y1: 2.30, sweep: [-24, 20] }),
    damage: 96, poise: 120, knock: 1.8, drag: 3.2, radial: 0.3,
    unblockable: true, gb: true, stun: 0.9,
    hitstop: 'heavy', shake: 0.52, kick: 0.40, hitCap: 6,
    root: { d: 0.50, a: 8, b: 34, ease: 'outQuint' },
    charge: 'ch3', vfx: 'slam', sfx: 'guard_break', tags: 'stun',
  }),
  /* CH3 — a 360 that reaches 1.9 m. Every other weapon's sweep360 clears a
   * circle; this one clears a doorway. */
  mv({
    id: 'ch3', label: 'Whirl of Two', clip: 'kapak_ch3', rate: 1.00,
    windup: 20, active: 32, recovery: 12,
    arc: A({ half: 180, bias: 0, r0: 0.25, r1: 1.90, y0: 0.10, y1: 2.20, sweep: [0, 720] }),
    damage: 36, poise: 40, knock: 1.4, drag: 3.4, radial: 0.9,
    hitstop: 'med', shake: 0.34, kick: 0.24, hitCap: 24,
    root: { d: 1.10, a: 6, b: 46, ease: 'outQuint' },
    charge: 'ch4', vfx: 'slash', sfx: 'swing_huge', tags: 'aoe',
  }),
  mv({
    id: 'ch4', label: 'Beak Drop', clip: 'kapak_ch4', rate: 1.00,
    windup: 32, active: 10, recovery: 18,
    arc: A({ half: 64, bias: 0, r0: 0.25, r1: 1.70, y0: -0.15, y1: 2.40, sweep: [-34, 30] }),
    damage: 82, poise: 104, knock: 2.6, slam: true, drag: 3.0, radial: 0.35,
    hitstop: 'heavy', shake: 0.50, kick: 0.38, hitCap: 8,
    root: { d: 0.80, a: 8, b: 32, ease: 'outQuint' },
    charge: 'ch5', vfx: 'slam', sfx: 'impact_slam', tags: 'wallSlam',
  }),
  /* CH5 — Inside Storm. 50 active frames and root travel of 0.45 m: it does
   * not chase, because on this weapon anything worth hitting is already
   * within arm's reach. */
  mv({
    id: 'ch5', label: 'Inside Storm', clip: 'kapak_ch5', rate: 1.00,
    windup: 19, active: 50, recovery: 14,
    arc: A({ half: 130, bias: 0, r0: 0.25, r1: 1.80, y0: 0.10, y1: 2.10, sweep: [-120, 120] }),
    damage: 30, poise: 28, knock: 1.0, drag: 3.2, radial: 0.75,
    hitstop: 'light', shake: 0.26, kick: 0.20, hitCap: 30,
    root: { d: 0.45, a: 6, b: 60, ease: 'outQuint' },
    charge: null, vfx: 'slash', sfx: 'swing_light', tags: 'rush',
  }),

  /* --- air, dash, musou -------------------------------------------------- */
  mv({
    id: 'air1', label: 'Falling Rend', clip: 'kapak_c1', rate: 1.10,
    windup: 6, active: 4, recovery: 9,
    arc: A({ half: 70, bias: 0, r0: 0.25, r1: 1.55, y0: -0.60, y1: 1.80, sweep: [-50, 40] }),
    damage: 30, poise: 26, knock: 1.0, drag: 3.0, radial: 0.35,
    hitstop: 'light', shake: 0.22, hitCap: 8,
    next: 'air2', vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    id: 'air2', label: 'Falling Cross', clip: 'kapak_c3', rate: 1.10,
    windup: 8, active: 5, recovery: 10,
    arc: A({ half: 96, bias: 0, r0: 0.25, r1: 1.65, y0: -0.60, y1: 1.90, sweep: [-84, 80] }),
    damage: 36, poise: 32, knock: 1.1, drag: 3.2, radial: 0.5,
    hitstop: 'med', shake: 0.26, hitCap: 12,
    vfx: 'slash', sfx: 'swing_light',
  }),
  mv({
    /* rate 1.45, not 1.05: ch4's hitboxOn marker sits at frame 32, and a
     * 31-frame windup hanging in the air is a different move from the one
     * intended. Speeding the playback brings the marker to 22 rather than
     * moving the window away from it — moveset-drift caught the 9-frame gap. */
    id: 'airSlam', label: 'Garuda Falls', clip: 'kapak_ch4', rate: 1.45,
    windup: 22, active: 7, recovery: 15,
    arc: A({ half: 180, bias: 0, r0: 0.25, r1: 2.00, y0: -0.40, y1: 2.20, sweep: [0, 0] }),
    damage: 80, poise: 100, knock: 2.4, slam: true, drag: 3.0, radial: 0.8,
    unblockable: true,
    hitstop: 'heavy', shake: 0.50, kick: 0.38, hitCap: 14,
    vfx: 'slam', sfx: 'impact_slam',
  }),
  mv({
    id: 'dash', label: 'Closing Rend', clip: 'kapak_c4', rate: 1.15,
    windup: 9, active: 5, recovery: 10,
    arc: A({ half: 60, bias: 0, r0: 0.25, r1: 1.80, y0: 0.10, y1: 2.00, sweep: [-30, 26] }),
    damage: 40, poise: 40, knock: 1.2, drag: 3.4, radial: 0.3,
    hitstop: 'med', shake: 0.30, hitCap: 8,
    root: { d: 2.40, a: 3, b: 18, ease: 'outQuint' },
    vfx: 'slash', sfx: 'swing_heavy',
  }),
  mv({
    id: 'musouFinish', label: 'Garuda Descends', clip: 'kapak_musou', rate: 1.00,
    windup: 19, active: 58, recovery: 16,
    arc: A({ half: 180, bias: 0, r0: 0.25, r1: 2.40, y0: -0.10, y1: 2.60, sweep: [0, 1080] }),
    damage: 120, poise: 200, knock: 3.0, lift: 2.4, launch: true,
    unblockable: true, gb: true, drag: 3.6, radial: 1.0,
    hitstop: 'heavy', shake: 0.70, kick: 0.50, hitCap: 40,
    root: { d: 0.90, a: 6, b: 74, ease: 'outQuint' },
    vfx: 'musouBurst', sfx: 'impact_musou',
  }),
]);

export const MOVESETS = {
  greatGlaive: {
    name: 'greatGlaive', label: 'Great Glaive', weapon: 'greatGlaive',
    // `reach` is documentation: the measured blade-tip reach from the pelvis
    // at the peak of the set's longest strike (see docs, tools/_tmp probes).
    reach: 2.2, weight: 1.0,
    chain: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'],
    charges: ['ch1', 'ch2', 'ch3', 'ch4', 'ch5'],
    air: ['air1', 'air2'], airHeavy: 'airSlam', dash: 'dash',
    musou: musou({}),
    moves: GLAIVE,
  },
  spear: {
    name: 'spear', label: 'Spear', weapon: 'spear',
    reach: 2.2, weight: 0.8,   // + 2.6 m of root travel inside the window
    chain: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'],
    charges: ['ch1', 'ch2', 'ch3', 'ch4', 'ch5'],
    air: ['air1', 'air2'], airHeavy: 'airSlam', dash: 'dash',
    musou: musou({ rate: 1.15, duration: 3.4, spin: 120, pulse: Object.assign({}, musou({}).pulse, { every: 6, damage: 24, r1: 4.6 }) }),
    moves: SPEAR,
  },
  twinSabres: {
    // `offhand` exists so a moveset knows the whole of what it is holding.
    // Without it the only place that knew the twin sabres are TWO blades was
    // the roster row, and anything that changed weapon through
    // player.setWeapon() left the hero swinging the previous model.
    name: 'twinSabres', label: 'Twin Sabres', weapon: 'dao', offhand: 'dao',
    reach: 1.5, weight: 0.6,
    chain: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'],
    charges: ['ch1', 'ch2', 'ch3', 'ch4', 'ch5'],
    air: ['air1', 'air2'], airHeavy: 'airSlam', dash: 'dash',
    musou: musou({
      rate: 1.30, duration: 4.2, spin: 130,
      pulse: Object.assign({}, musou({}).pulse, { every: 5, damage: 18, arc: A({ half: 180, r0: 0.05, r1: 3.2, y0: -0.20, y1: 2.40 }) }),
    }),
    moves: SABRES,
  },
  kapak: {
    /* Paired, like the sabres: the same axe in both fists. `offhand` is what
     * makes setPlayerWeapon() attach the second one; without it the hero
     * swings one axe and an empty hand. */
    name: 'kapak', label: 'Kapak Garuda', weapon: 'kapak', offhand: 'kapak',
    /* The shortest reach in the game, by a margin, and the whole point of the
     * weapon. The sabres were previously the shortest at 1.5. */
    reach: 1.1, weight: 1.5,
    chain: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'],
    charges: ['ch1', 'ch2', 'ch3', 'ch4', 'ch5'],
    air: ['air1', 'air2'], airHeavy: 'airSlam', dash: 'dash',
    musou: musou({
      rate: 1.25, duration: 3.8, spin: 150,
      pulse: Object.assign({}, musou({}).pulse, { every: 5, damage: 22, r1: 2.4 }),
    }),
    moves: KAPAK,
  },
  gada: {
    name: 'gada', label: 'Gada', weapon: 'gada',
    /* The shortest reach of any two-handed weapon here, and deliberately so:
     * measured from the pelvis at the peak of the longest light-chain strike,
     * the head gets to 2.19 m where the glaive's blade gets to 3.28. Weight is
     * the heaviest in the set — it is what the HUD and the roster read to say
     * "slow" before the player has swung it once. */
    reach: 1.9, weight: 1.4,
    chain: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'],
    charges: ['ch1', 'ch2', 'ch3', 'ch4', 'ch5'],
    air: ['air1', 'air2'], airHeavy: 'airSlam', dash: 'dash',
    /* A slower grind that hits harder and reaches less far, which is the whole
     * weapon restated: fewer, heavier pulses inside the same gauge. */
    musou: musou({
      rate: 0.88, duration: 3.8, spin: 105,
      pulse: Object.assign({}, musou({}).pulse, { every: 10, damage: 52, r1: 3.9, lift: 0.75, knock: 1.6 }),
    }),
    moves: GADA,
  },
  cambuk: {
    name: 'cambuk', label: 'Cambuk Naga', weapon: 'cambuk',
    /* The longest reach in the game and the only inner DEAD ZONE: every arc
     * opens at r0 1.1-1.3 where the other four open at 0.25-0.45. Weight is
     * the lightest — a chain is swung from the wrist. */
    reach: 3.6, weight: 0.5,
    chain: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'],
    charges: ['ch1', 'ch2', 'ch3', 'ch4', 'ch5'],
    air: ['air1', 'air2'], airHeavy: 'airSlam', dash: 'dash',
    /* Fast, shallow pulses over a huge ring: the grind is the weapon. */
    musou: musou({
      rate: 1.10, duration: 4.4, spin: 150,
      pulse: Object.assign({}, musou({}).pulse, {
        every: 5, damage: 20, r0: 1.0, r1: 5.4, lift: 0.4, knock: 0.9,
      }),
    }),
    moves: CAMBUK,
  },
};

/* =========================================================================
 *  BRAVERY — the cost that makes the charge branches a choice
 * -------------------------------------------------------------------------
 *  DEV_PLAN Step 3. Until this table existed the five charge branches were
 *  free and always available, which means picking between them was not an
 *  allocation — it was a button. The Step 0 verb census measured exactly that
 *  and named it the structural ceiling on `decisionDensity`: twenty-one verbs,
 *  ONE of them priced.
 *
 *  Every number a player feels about bravery lives here, including the earn
 *  rates that player.js applies, because a resource tuned from two files drifts
 *  the first time one of them is touched alone.
 *
 *  THE SPEND LADDER IS THE POINT. A charge costs 1 and the Battle Art costs 3,
 *  so a full pool is either three charges now or one art soon, and that
 *  question is the decision the row is asking about. One tier would only have
 *  been a tax.
 * ========================================================================= */
export const BRAVERY = {
  /** Fallback for a charge branch with no role tag. See `byRole`. */
  charge: 1,
  /**
   * THE BRANCH PRICE FOLLOWS THE ROLE, NOT THE SLOT.
   *
   * Until this table existed all five branches cost 1, and the Step 3 verdict
   * named that as what held `decisionDensity` at 5: the live question was
   * "spend or save for the Art" and never "WHICH branch", so the branch choice
   * — the thing five separate moves exist to offer — was still free.
   *
   * Every weapon carries the same five roles, so the price can be a property of
   * what a move DOES rather than of where it sits in the list, and a sixth
   * weapon is priced correctly the moment it is tagged:
   *
   *   stun      1  buys a beat, kills nothing
   *   launcher  1  opens the juggle; the chain's workhorse, kept affordable
   *   sweep360  2  crowd control at 180 degrees
   *   wallSlam  2  the gap-close, and the biggest single-target number (86-110)
   *   aoe       3  the widest ring in the set — AND THE SAME PRICE AS THE ART,
   *                which is what makes "which" a live question rather than a
   *                sequence. At the rank-1 cap of 4 you may have one ring and
   *                one cheap move, two middling ones, or four small ones.
   */
  byRole: { stun: 1, launcher: 1, sweep360: 2, wallSlam: 2, rush: 2, aoe: 3 },
  art: 3,           // the Battle Art
  /* Earned. ~17 connected hits or 8 blows held on guard buys a charge: a
   * packed C4 lands about 25 hits, so a good sweep pays for the next branch
   * and a whiffed one does not. Guarding pays nearly ten times a hit's rate
   * per EVENT, which is what makes standing in a blow a live option rather
   * than a last resort — and it is the only thing in the game that currently
   * rewards defence at all. */
  perHit: 0.06,
  perGuard: 0.5,
  /* DEV_PLAN Step 4. A parry and a perfect dodge each pay a whole charge —
   * twice what merely holding guard pays, and about seventeen hits' worth.
   * That ratio is the entire argument for defending: the fastest way to afford
   * a charge is to read one blow correctly, not to swing at seventeen men. */
  perParry: 1.0,
  perPerfectDodge: 1.0,
  /* The cap, 4 at rank 1 rising to 10. One more point every two ranks, so the
   * pool widens across roughly the first dozen ranks and then holds. */
  base: 4,
  max: 10,
  ranksPerPoint: 2,
};

/* =========================================================================
 *  DEFENCE — the two windows (DEV_PLAN Step 4)
 * -------------------------------------------------------------------------
 *  A tight window at the START of a guard reads as a parry; a tight window at
 *  the start of a dodge, against a hitbox that would genuinely have connected,
 *  reads as a perfect dodge. Both pay bravery, so defence finally feeds the
 *  economy Step 3 built instead of only avoiding loss.
 *
 *  EIGHT FRAMES is the parry window, and it is deliberately at the tight end.
 *  The reference games sit around 6-12: too wide and holding guard becomes a
 *  parry by accident, which removes the read rather than rewarding it; too
 *  narrow and it is a lottery. This is a number the probe MEASURES rather than
 *  trusts — `critic_defence` sweeps the delay between guard start and the blow
 *  and reports the last frame that still parries, so a refactor that silently
 *  widened it would be caught by the frame count and not by feel.
 *
 *  Ten frames for the perfect dodge, slightly wider: a dodge commits the
 *  player to a direction and a recovery, so it is the riskier read of the two
 *  and is paid for at the same rate.
 * ========================================================================= */
export const DEFENCE = {
  parryWindow: 8 / 60,
  dodgeWindow: 10 / 60,
  /* A parry buys i-frames past the window so the follow-up of a combo does not
   * land in the recovery of the read that beat its first blow. */
  parryIFrames: 0.22,
  /* What the parried attacker pays. `world.ai.stagger()` is the documented
   * path for this and a parry must not reach past it. */
  parryStagger: 0.55,
};

/** The bravery cap a given career rank allows. */
export function braveryCapFor(rank) {
  const r = (rank > 1 ? rank : 1) | 0;
  const n = BRAVERY.base + Math.floor((r - 1) / BRAVERY.ranksPerPoint);
  return n > BRAVERY.max ? BRAVERY.max : n;
}

/* Stamp the price onto every charge branch, and give every weapon a Battle
 * Art, in ONE place. Writing `bravery: 1` into fifteen hand-maintained move
 * descriptors is how a sixth weapon ships free charges and nobody notices —
 * and the verb census in tools/scenarios.mjs reads the cost off the live
 * descriptor, so anything missed here would silently read as a free verb. */
/** What one charge branch costs, from its role tag. */
export function braveryPriceFor(move) {
  if (!move) return 0;
  for (const role in BRAVERY.byRole) {
    if ((move.tags || '').indexOf(role) >= 0) return BRAVERY.byRole[role];
  }
  return BRAVERY.charge;
}

for (const setName of Object.keys(MOVESETS)) {
  const set = MOVESETS[setName];
  for (let i = 0; i < set.charges.length; i++) {
    const mv = set.moves[set.charges[i]];
    if (mv) mv.bravery = braveryPriceFor(mv);
  }
  /* The Battle Art is the musou's finisher, spent on its own. It is an
   * authored move with real properties — a 9 m arc at 110 damage — rather than
   * a new clip this step has no business authoring, and it sits exactly where
   * a burst tier belongs: above a charge, below a musou. */
  set.battleArt = { id: set.musou.finish, bravery: BRAVERY.art, label: 'Battle Art' };
  const art = set.moves[set.musou.finish];
  if (art) art.bravery = BRAVERY.art;
}

export const MOVESET_NAMES = Object.keys(MOVESETS);

/** Look a move up by weapon + id. Falls back to the musou finisher. */
export function getMove(setName, id) {
  const s = MOVESETS[setName];
  if (!s || !id) return null;
  return s.moves[id] || null;
}

/**
 * Sanity pass, run once from combat.init(). Reports (never throws) any move
 * whose clip does not exist or whose declared frames do not agree with the
 * clip's own hitbox markers, so a retimed clip cannot silently desync the
 * table. `clipInfo(name) -> { dur, on, off }` in seconds, from world.anim.
 */
export function validateMovesets(clipInfo) {
  const problems = [];
  for (const sn of MOVESET_NAMES) {
    const S = MOVESETS[sn];
    for (const id in S.moves) {
      const m = S.moves[id];
      const info = clipInfo(m.clip);
      if (!info) { problems.push(`${sn}.${id}: missing clip '${m.clip}'`); continue; }
      const clipFrames = info.dur * 60 / m.rate;
      if (m.total > clipFrames + 2.5) {
        problems.push(`${sn}.${id}: ${m.total}f declared but clip runs ${clipFrames.toFixed(1)}f at rate ${m.rate}`);
      }
      if (!m.ownWindows && info.on >= 0) {
        const onF = info.on * 60 / m.rate;
        if (Math.abs(onF - m.windup) > 4.5) {
          problems.push(`${sn}.${id}: windup ${m.windup}f vs marker ${onF.toFixed(1)}f`);
        }
      }
    }
    if (!S.moves[S.musou.finish]) problems.push(`${sn}: musou finisher '${S.musou.finish}' missing`);
  }
  return problems;
}
